import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Loader2, Copy, CheckCircle2, X, Grid2x2, Trash2, Flame, Trophy, Zap, Calendar, RefreshCw, SkipForward, Plus, ChevronUp, ChevronDown, Clock, FileText, Archive, Download, Check, ChevronRight, FileSignature, Mic, MicOff, Settings2, Search, BookmarkPlus, Bookmark, RotateCcw, AlertTriangle, Eye, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import HourglassIcon from './HourglassIcon';
import axios from 'axios';
import { safeCopyToClipboard } from '../../utils/clipboard';
import { getHistoryDisplayTitle, formatHistoryTime } from '../../hooks/historyDisplayUtils';
import { DOT_PHRASES } from './NotesInput';
import { isNetworkError, stashNetworkDraft, getNetworkDrafts, removeNetworkDraft } from '../../utils/networkDrafts';
import { detectIcd10Hints } from '../../utils/icd10HintEngine';
import { persistProfilePref } from '../../utils/persistProfilePref';

const API = window.__WR_API_BASE__;

// Feb 27, 2026 (v6.8.21) — Hardened title casing.
// Previously `firstChar.toUpperCase() + rest.toLowerCase()` produced
// garbled output for ALL-CAPS or mixed-case words extracted by the
// regex pipeline (e.g., raw "ACCOMPLished" → unchanged-looking
// "Accomplished" most of the time, but stray characters surfaced as
// the user-reported "OMPlished?" — a typo or partial match leaking
// through). Now:
//   • Trailing/leading non-letter punctuation is stripped from each word
//   • Words shorter than 4 chars that are ALL-CAPS are preserved as
//     acronyms (e.g., "MRI", "ICD", "TBI", "LBP")
//   • Everything else gets normal title case
//   • The whole string is trimmed of stray ?, ., !, , at start/end
// Feb 27, 2026 (v6.8.21) — Military time helper.
// Provider feedback: "the times are weird... 2026-03-05 15:10 AM" — that
// was the locale toLocaleTimeString mixing 24-hour digits with the AM/PM
// suffix in some browser/OS locales. Standardize on `HHMM hrs` since this
// is a military medical charting tool — `1510 hrs` reads cleaner than
// `3:10 PM` AND is consistent with DoD documentation conventions.
// Handles both raw Date objects and HTML <input type="time"> strings
// (which arrive as "HH:MM" 24-hr already).
const toMilitaryTime = (input) => {
  if (input) {
    // HTML time input format: "HH:MM" or "HH:MM:SS"
    const m = String(input).match(/^(\d{1,2}):(\d{2})/);
    if (m) {
      const hh = String(parseInt(m[1], 10)).padStart(2, '0');
      const mm = m[2];
      return `${hh}${mm} hrs`;
    }
    // Already-formatted "HHMM hrs" — pass through unchanged.
    if (/^\d{4}\s*hrs$/i.test(String(input).trim())) return String(input).trim();
  }
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}${mm} hrs`;
};

const smartTitle = (str) => {
  if (!str) return '';
  const lower = ['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'from', 'up', 'about', 'into', 'through', 'during', 'before', 'after', 'above', 'below', 'between', 'under', 'since', 'without', 'per', 'vs'];
  // Strip stray surrounding punctuation while preserving internal slashes
  // (e.g., "R/O", "B/L") and hyphens (e.g., "low-back").
  const cleaned = str.replace(/^[\s\W_]+|[\s.,?!;:_]+$/g, '');
  return cleaned.split(/\s+/).map((wRaw, i) => {
    // Strip stray punctuation from word edges; keep mid-word slashes/dashes.
    const w = wRaw.replace(/^[^\w/-]+|[^\w/-]+$/g, '');
    if (!w) return '';
    // Preserve common short medical acronyms (all uppercase, 2-4 chars).
    if (/^[A-Z]{2,4}$/.test(w)) return w;
    if (i > 0 && lower.includes(w.toLowerCase())) return w.toLowerCase();
    // Standard title case for everything else — capital first letter, lower rest.
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).filter(Boolean).join(' ');
};

const TITLE_FORMAT_OPTIONS = [
  { value: 'date_topic', label: 'Date + topic', sample: '2/16/2026 — Low back pain' },
  { value: 'date', label: 'Date only', sample: '2/16/2026' },
  { value: 'date_time', label: 'Date + time', sample: '2/16/2026 14:30' },
  { value: 'topic', label: 'Topic only', sample: 'Low back pain' },
  { value: 'blank', label: 'No title', sample: '(no header)' },
];

const generatePanelTitle = (panel, idx, format = 'date_topic') => {
  // Compute the topic regardless of format selection so we can reuse below.
  const notes = panel.notes || '';
  let topic = '';
  if (notes.trim()) {
    // Feb 27, 2026 (v6.8.22) — Word boundaries on short abbreviations.
    // Previously `cc` matched mid-word inside "aCComplished" (from the
    // telehealth boilerplate) and the capture group started at offset 3,
    // producing the garbled title "Omplished Via Virtual Health
    // Capabilities". Same risk for `cc`, `dx`, `hpi`, `c/o` — wrapping
    // them in \b...\b ensures they only match as standalone tokens.
    const patterns = [
      /(?:\bc\/o\b|\bcc\b|\bchief complaint\b|\bpresents? (?:with|for)\b|\bhere for\b|\bcomplains? of\b)\s*[:\-]?\s*(.+?)(?:\.|,|\n|for the|x\s*\d)/i,
      /(?:\bhpi\b|\bhistory\b)\s*[:\-]\s*(.+?)(?:\.|,|\n)/i,
      /(?:\bassessment\b|\bdx\b|\bdiagnosis\b|\bimpression\b)\s*[:\-]\s*(.+?)(?:\.|,|\n)/i,
      /^\s*(?:\d+[.\)]\s*)?(.+?)(?:\.|,|\n)/i,
    ];
    for (const re of patterns) {
      const m = notes.match(re);
      // Feb 27, 2026 (v6.8.22) — Lower the length floor to 2 chars so
      // common medical acronyms (LBP, MRI, TBI, ICD, HTN, DM, HA, BP)
      // can serve as the topic when they appear after `c/o`. The old
      // `> 3` cutoff rejected "LBP" and fell through to pattern 4, which
      // captured the whole telehealth boilerplate as the title.
      if (m && m[1] && m[1].trim().length >= 2) {
        topic = smartTitle(m[1].trim());
        break;
      }
    }
    if (!topic) {
      const first = notes.trim().split(/[\n.]/)[0].trim();
      topic = smartTitle(first.length > 60 ? first.substring(0, 60) : first);
    }
  }
  // Feb 16, 2026 — title format toggle in toolbar. Provider chooses which
  // shape suits their paste target (Genesis vs. handoff messages vs.
  // personal scratch). Custom panel label always wins when set.
  if (panel.label && panel.label.trim()) {
    return topic ? `${panel.label.trim()} — ${topic}` : panel.label.trim();
  }
  if (format === 'blank') return '';
  if (format === 'topic') return topic || '';
  const dateStr = panel.apptDate || new Date().toLocaleDateString();
  if (format === 'date') return dateStr;
  if (format === 'date_time') {
    const t = toMilitaryTime(panel.apptTime);
    return `${dateStr} ${t}`;
  }
  // default: date_topic
  const age = panel.age ? ` | ${panel.age}yo` : '';
  const gender = panel.gender ? ` ${panel.gender === 'male' ? 'M' : 'F'}` : '';
  return topic ? `${dateStr}${age}${gender} — ${topic}` : `${dateStr}${age}${gender}`;
};

const SPEED_CPT_OPTIONS = [
  { key: 'in_person', code: '99214', label: 'In-Person', shortLabel: 'IP' },
  { key: 'video', code: '98004', label: 'Video', shortLabel: 'Vid' },
  { key: 'audio', code: '98012', label: 'Audio', shortLabel: 'Aud' },
];

const EMPTY_PANEL = () => ({
  id: crypto.randomUUID(),
  label: '',
  notes: '',
  age: '',
  gender: '',
  status: 'active_duty',
  apptDate: '',
  apptTime: '',
  output: null,
  icd10: [],
  historyId: null,
  isGenerating: false,
  copied: false,
  genTime: 0,
  icd10Collapsed: true,
  modality: 'in_person',
  visitType: 'in_person_exam',
  // Feb 18, 2026 — provider-flagged orders that MUST be placed in MHS Genesis
  // before the encounter closes. Empty string = unchecked. Any content = the
  // user has explicitly opted in (no LLM auto-detection, which was confusing
  // results-review visits with new imaging requests). These get prepended to
  // the generated note output as a checklist so they can't be forgotten.
  ordersImaging: '',
  ordersReferrals: '',
  // Profile reminder is a separate batch task (MEDPROS in a different system,
  // not Genesis), so we surface it on a slightly different border treatment
  // but it still lands in the same "ORDERS TO PLACE" block at the top of
  // the note + the end-of-day batch copy.
  ordersProfile: '',
  // Feb 27, 2026 (v6.8.17) — Track the LLM's ORIGINAL output for this panel
  // so we can detect manual edits during a Regenerate and pass them back as
  // `prior_output_edits` (preserves all clinical detail the provider added).
  // Also used by the "↺ Reset to LLM" affordance and the "EDITED" badge.
  outputOriginal: null,
  // Speed Mode v6.8.17 — show/hide notes overlay in post-output view so the
  // provider can see what was dictated without losing the editable output.
  notesPeek: false,
  // Per-panel telemetry — wall-clock ms of the most recent generate.
  lastGenMs: 0,
  // Feb 27, 2026 (v6.8.18) — Streaming state.
  // isStreaming: true while SSE chunks are arriving (Stop button shows).
  // ttfMs: time-to-first-token in ms (rendered in telemetry pill).
  isStreaming: false,
  ttfMs: 0,
  // Feb 27, 2026 (v6.8.19) — Modality manual-override lock.
  // True once the provider has clicked any IP/VH/Vid/Aud button. Disables
  // the notes-based modality auto-detector for the rest of this panel so
  // manual selection always wins. Cleared by Next Patient / panel reset.
  // Feb 27, 2026 (v6.8.23) — Live mic interim transcript buffer.
  // Holds the un-finalized speech while the recognizer is still
  // processing the current utterance. Rendered as a read-only italic
  // strip below the textarea; merged into `notes` on isFinal.
  interimText: '',
  modalityManuallySet: false,
});

const BatchChartingGrid = ({ user, guestId, onClose, history, clinicalTone }) => {
  const [gridSize, setGridSize] = useState(() => {
    const saved = localStorage.getItem('writeright_speed_grid');
    return saved === '2' ? 2 : 4;
  });
  // Feb 16, 2026 — title format toggle. Persists per provider via localStorage.
  // Valid: 'date_topic' (default) | 'date' | 'date_time' | 'topic' | 'blank'
  const [titleFormat, setTitleFormat] = useState(() => {
    const saved = localStorage.getItem('writeright_speed_title_format');
    return ['date_topic', 'date', 'date_time', 'topic', 'blank'].includes(saved) ? saved : 'date_topic';
  });
  useEffect(() => {
    try { localStorage.setItem('writeright_speed_title_format', titleFormat); } catch { /* ignore */ }
  }, [titleFormat]);
  const [panels, setPanels] = useState(() => {
    // Restore panels from localStorage if available
    try {
      const savedPanels = localStorage.getItem('writeright_speed_panels');
      if (savedPanels) {
        const parsed = JSON.parse(savedPanels);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Restore but reset transient state
          return parsed.map(p => ({ ...p, isGenerating: false, copied: false, genTime: 0 }));
        }
      }
    } catch (e) { /* ignore parse errors */ }
    const saved = localStorage.getItem('writeright_speed_grid');
    const size = saved === '2' ? 2 : 4;
    return Array.from({ length: size }, () => EMPTY_PANEL());
  });
  const [chartsCleared, setChartsCleared] = useState(0);
  const [sessionStart] = useState(Date.now());
  const [sessionElapsed, setSessionElapsed] = useState(0);
  const [timerPaused, setTimerPaused] = useState(false);
  const [pausedAt, setPausedAt] = useState(0);
  const [streakFlash, setStreakFlash] = useState(null);
  const [showHistoryPicker, setShowHistoryPicker] = useState(null); // panel idx or null
  const lastMilestone = useRef(0);
  const pauseOffset = useRef(0);

  // Load custom dot phrases
  const [customPhrases, setCustomPhrases] = useState({});
  useEffect(() => {
    axios.get(`${API}/api/dot-phrases`, { withCredentials: true })
      .then(res => {
        const map = {};
        (res.data.phrases || []).forEach(p => { map[p.keyword] = p.content; });
        setCustomPhrases(map);
      })
      .catch(() => {});
  }, []);

  // Auto-save panels to localStorage so they persist across navigation
  useEffect(() => {
    // Only save non-transient data (notes, output, icd10, etc.)
    const saveable = panels.map(p => ({
      ...p,
      isGenerating: false,
      copied: false,
      genTime: 0,
    }));
    localStorage.setItem('writeright_speed_panels', JSON.stringify(saveable));
  }, [panels]);


  // --- Speed Mode Session History ---
  const [sessionHistory, setSessionHistory] = useState(() => {
    try {
      const saved = localStorage.getItem('writeright_speed_session');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only restore if session is less than 12 hours old
        if (parsed.timestamp && Date.now() - parsed.timestamp < 12 * 60 * 60 * 1000) {
          return parsed.notes || [];
        }
      }
    } catch {}
    return [];
  });
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const [selectedSessionNotes, setSelectedSessionNotes] = useState(new Set());

  // Smart ICD-10 auto-detect settings
  const [smartIcd10Enabled, setSmartIcd10Enabled] = useState(() => {
    const stored = localStorage.getItem('writeright_speed_smart_icd10');
    return stored === null ? true : stored === 'true';
  });
  const [detectingIcd10, setDetectingIcd10] = useState({});
  const detectTimers = useRef({});
  // Feb 27, 2026 (v6.8.19) — Modality auto-detect debounce timers
  // (separate from ICD-10 since they fire on different latencies — 700ms
  // vs 2000ms — and have different gating).
  const modalityDetectTimers = useRef({});

  // Voice dictation state (per-panel)
  const [recordingPanel, setRecordingPanel] = useState(null);
  const recognitionRef = useRef(null);

  // Feb 18, 2026 — Per-panel MediaRecorder so we capture the raw audio in
  // parallel with browser SpeechRecognition. The browser SR is great for
  // instant on-screen feedback but its quality is mediocre; the audio blob
  // here is what powers the "AI Cleanup" button — posts to /api/transcribe
  // (Deepgram + Universal AI cleanup) for high-quality replacement.
  const mediaRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const [audioBlobs, setAudioBlobs] = useState({});           // { [idx]: Blob }
  const [aiCleanupBusy, setAiCleanupBusy] = useState({});     // { [idx]: boolean }

  // Feb 26, 2026 — Batch-level recording mode for AI Cleanup.
  // Shares the same localStorage key as the main provider dictation so the
  // last-used mode survives the trip into Speed Mode. One choice for the
  // whole batch (matches the "Mondays = phone, Tuesdays = in-person" pattern).
  const [batchRecordingMode, setBatchRecordingModeState] = useState(() => {
    try {
      const saved = localStorage.getItem('writeright_recording_mode');
      if (saved === 'solo' || saved === 'in_person' || saved === 'phone') return saved;
    } catch (e) { /* ignore */ }
    return 'solo';
  });
  const setBatchRecordingMode = useCallback((m) => {
    const next = (m === 'solo' || m === 'in_person' || m === 'phone') ? m : 'solo';
    setBatchRecordingModeState(next);
    try { localStorage.setItem('writeright_recording_mode', next); } catch (e) { /* ignore */ }
    persistProfilePref({ recording_mode: next });
  }, []);

  // Feb 18, 2026 — Focus dimming: which panel the provider is currently
  // working in. Non-focused panels fade to ~50% opacity to reduce visual
  // noise on the 4x4 grid. Switching focus is via click/textarea-focus.
  // Setting to null lights every panel back to full brightness ("show all").
  const [focusedPanel, setFocusedPanel] = useState(null);
  // Feb 27, 2026 (v6.8.17) — Active/current panel for the "NEXT ↓" indicator
  // and post-batch telemetry pill (proves parallel-vs-serial for Generate All).
  const [activePanelIdx, setActivePanelIdx] = useState(0);
  const [lastBatchTelemetry, setLastBatchTelemetry] = useState(null);
  const [focusModeEnabled, setFocusModeEnabled] = useState(() => {
    const stored = localStorage.getItem('writeright_speed_focus_mode');
    return stored === 'true';
  });
  useEffect(() => {
    localStorage.setItem('writeright_speed_focus_mode', String(focusModeEnabled));
  }, [focusModeEnabled]);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(null); // panel idx

  // CPT Combo Savebook (shared with standard mode)
  const [cptCombos, setCptCombos] = useState([]);
  const [showCptCombos, setShowCptCombos] = useState(null); // panel idx or null

  // Persist smart ICD-10 setting
  useEffect(() => {
    localStorage.setItem('writeright_speed_smart_icd10', String(smartIcd10Enabled));
  }, [smartIcd10Enabled]);

  // Load templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const params = guestId ? `?guest_id=${guestId}` : '';
        const res = await axios.get(`${API}/api/speed-templates${params}`, { withCredentials: true });
        setTemplates(res.data.templates || []);
      } catch (e) { /* silent */ }
    };
    const loadCombos = async () => {
      try {
        const params = guestId ? `?guest_id=${guestId}` : '';
        const res = await axios.get(`${API}/api/cpt-combos${params}`, { withCredentials: true });
        setCptCombos(res.data.combos || []);
      } catch (e) { /* silent */ }
    };
    loadTemplates();
    loadCombos();
  }, [guestId]);

  // Smart ICD-10 auto-detect — debounced per panel
  const triggerIcd10Detect = useCallback((idx, text) => {
    if (!smartIcd10Enabled || !text || text.trim().length < 30) return;
    // Clear previous timer for this panel
    if (detectTimers.current[idx]) clearTimeout(detectTimers.current[idx]);
    detectTimers.current[idx] = setTimeout(async () => {
      setDetectingIcd10(prev => ({ ...prev, [idx]: true }));
      try {
        const res = await axios.post(`${API}/api/icd10/detect`, {
          text: text,
          max_codes: 6,
        }, { withCredentials: true, timeout: 15000 });
        const codes = res.data.codes || [];
        if (codes.length > 0) {
          setPanels(prev => prev.map((p, i) => {
            if (i !== idx || p.output) return p; // Don't overwrite post-gen codes
            return { ...p, icd10: codes };
          }));
        }
      } catch (e) { /* silent */ }
      setDetectingIcd10(prev => ({ ...prev, [idx]: false }));
    }, 2000); // 2 second debounce
  }, [smartIcd10Enabled]);

  // Feb 27, 2026 (v6.8.19) — Modality auto-detect from notes.
  // Provider asked: "writing vh in the note does not change the tab
  // selected in speed mode." Light-touch regex scan after a 700ms debounce.
  // Only auto-switches if the provider hasn't manually toggled the
  // modality (modalityManuallySet flag). Once they click any IP/VH/Vid/
  // Aud button, this detector goes silent for that panel for the rest of
  // the encounter — manual override always wins.
  //
  // Match priority (first match wins):
  //   audio-only:  "audio only" | "phone visit" | "telephone" | \baud\b
  //   video:       "video visit" | "vh" | "virtual" | "televisit" |
  //                "telehealth" | "telemed" | "secure messaging" (results)
  //   in-person:   "in person" | "in-person" | \bip\b (only if currently
  //                virtual — don't ping-pong)
  const triggerModalityDetect = useCallback((idx, text) => {
    if (!text || text.trim().length < 4) return;
    if (modalityDetectTimers.current[idx]) clearTimeout(modalityDetectTimers.current[idx]);
    modalityDetectTimers.current[idx] = setTimeout(() => {
      setPanels(prev => prev.map((p, i) => {
        if (i !== idx) return p;
        if (p.modalityManuallySet) return p; // honor manual override
        const t = (p.notes || '').toLowerCase();
        // Audio-only patterns
        const audioRe = /\baudio\s*only\b|\bphone\s+visit\b|\btelephone\b|\baudio\s+visit\b/;
        // Video / virtual patterns (most common)
        const videoRe = /\bvh\b|\bvirtual\s+health\b|\bvirtual\s+visit\b|\bvideo\s+visit\b|\btele(?:visit|health|medicine|med)\b|\bvirtual\b/;
        // In-person revert (only if currently virtual — avoid loop)
        const inPersonRe = /\bin[-\s]?person\b|\boffice\s+visit\b|\bclinic\s+visit\b/;
        let next = null;
        if (audioRe.test(t)) {
          next = { modality: 'audio', visitType: 'virtual' };
        } else if (videoRe.test(t)) {
          next = { modality: 'video', visitType: 'virtual' };
        } else if (inPersonRe.test(t) && (p.modality === 'video' || p.modality === 'audio')) {
          next = { modality: 'in_person', visitType: 'in_person_exam' };
        }
        if (!next) return p;
        // Only return a new object if something actually changed.
        if (next.modality === p.modality && next.visitType === p.visitType) return p;
        return { ...p, ...next };
      }));
    }, 700);
  }, []);

  // Voice dictation — lightweight browser SpeechRecognition per panel.
  // We ALSO open a MediaRecorder in parallel so the audio blob is available
  // for "AI Cleanup" (Deepgram + Universal AI rewrite) after the user stops.
  const startDictation = useCallback(async (idx) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }
    // Stop any existing dictation
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
      setRecordingPanel(null);
    }
    // Stop any prior MediaRecorder (defensive)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }

    // Open a single mic stream — feeds both SpeechRecognition (live) and
    // MediaRecorder (for AI Cleanup post-processing).
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
    } catch (e) {
      toast.error('Microphone permission denied');
      return;
    }
    // Pick the best mime type the browser supports
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
    const mime = candidates.find(m => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
    try {
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: mr.mimeType || mime || 'audio/webm' });
        if (blob.size > 0) {
          setAudioBlobs(prev => ({ ...prev, [idx]: blob }));
        }
      };
      mr.start(500);  // collect chunks every 500ms
      mediaRecorderRef.current = mr;
    } catch (e) {
      // If MediaRecorder fails, the SpeechRecognition still works — just no AI Cleanup
      console.warn('MediaRecorder unavailable:', e);
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      // Feb 27, 2026 (v6.8.23) — Live transcription bug fix.
      // Previously we only committed FINAL results to panel.notes. With
      // browser SpeechRecognition, "final" doesn't fire until the user
      // pauses for 1-3 seconds — so during continuous dictation the
      // textarea stayed empty and the provider thought the mic was dead
      // (required page refresh to see autosaved content). Now we also
      // surface the live INTERIM transcript in a separate `interimText`
      // field on the panel (rendered as a faint italic suffix), and
      // commit final chunks as before. The textarea reflects both the
      // committed `notes` PLUS the live `interimText` until the user
      // pauses, at which point the interim is empty and notes contains
      // everything.
      let finalText = '';
      let interimText = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) {
          finalText += res[0].transcript + ' ';
        } else {
          interimText += res[0].transcript;
        }
      }
      setPanels(prev => prev.map((p, pi) => {
        if (pi !== idx) return p;
        let next = { ...p, interimText };
        if (finalText.trim()) {
          const prefix = p.notes.length > 0 && !p.notes.endsWith(' ') && !p.notes.endsWith('\n') ? ' ' : '';
          next.notes = p.notes + prefix + finalText.trim();
          next.interimText = ''; // committed — clear interim
        }
        return next;
      }));
    };
    recognition.onerror = (e) => {
      if (e.error !== 'no-speech' && e.error !== 'aborted') {
        toast.error(`Mic error: ${e.error}`);
      }
    };
    recognition.onend = () => {
      // Auto-restart if still active
      if (recordingPanel === idx && recognitionRef.current) {
        try { recognition.start(); } catch (e) { setRecordingPanel(null); }
      }
    };
    recognition.start();
    recognitionRef.current = recognition;
    setRecordingPanel(idx);
    toast.success('Listening...', { duration: 2000 });
  }, [panels, recordingPanel]);

  const stopDictation = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    // Stop the MediaRecorder — onstop handler captures the blob.
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
    setRecordingPanel(null);
  }, []);

  // Feb 18, 2026 — AI Cleanup: post the captured audio blob from the panel
  // through /api/transcribe (Deepgram + Universal AI rewrite) and REPLACE
  // the panel notes with the cleaner version. Provides medical-grade
  // transcription quality that browser SpeechRecognition can't match.
  const aiCleanupPanel = useCallback(async (idx) => {
    const blob = audioBlobs[idx];
    if (!blob || blob.size === 0) {
      toast.error('No recorded audio for this panel. Tap the mic and dictate first.');
      return;
    }
    setAiCleanupBusy(prev => ({ ...prev, [idx]: true }));
    try {
      const form = new FormData();
      // Backend expects field name 'file' (see /api/transcribe in server.py)
      const ext = (blob.type.includes('mp4') || blob.type.includes('m4a')) ? 'm4a'
                : (blob.type.includes('ogg')) ? 'ogg'
                : 'webm';
      form.append('file', blob, `speed-mode.${ext}`);
      form.append('recording_mode', batchRecordingMode || 'solo');
      const res = await axios.post(`${API}/api/transcribe`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const cleaned = (res.data?.transcript || res.data?.text || '').trim();
      if (!cleaned) {
        toast.error('AI Cleanup returned empty text');
      } else {
        setPanels(prev => prev.map((p, pi) => (pi === idx ? { ...p, notes: cleaned } : p)));
        toast.success('AI-cleaned transcript replaced your notes');
      }
    } catch (e) {
      console.error('AI Cleanup failed:', e);
      toast.error(e?.response?.data?.detail || 'AI Cleanup failed — original notes kept');
    } finally {
      setAiCleanupBusy(prev => ({ ...prev, [idx]: false }));
    }
  }, [audioBlobs, batchRecordingMode]);

  // Save as template
  const saveAsTemplate = useCallback(async (idx) => {
    const panel = panels[idx];
    if (!panel.output) { toast.error('Generate a note first'); return; }
    const name = panel.label || generatePanelTitle(panel, idx, titleFormat) || `Template ${templates.length + 1}`;
    try {
      const res = await axios.post(`${API}/api/speed-templates`, {
        name,
        content: panel.output,
        icd10_codes: panel.icd10 || [],
        cpt_code: SPEED_CPT_OPTIONS.find(o => o.key === panel.modality)?.code,
        modality: panel.modality,
        guest_id: guestId,
      }, { withCredentials: true });
      setTemplates(prev => [res.data, ...prev]);
      toast.success(`Template "${name}" saved`);
      setShowSaveTemplate(null);
    } catch (e) {
      toast.error('Failed to save template');
    }
  }, [panels, templates.length, guestId]);

  // Delete template
  const deleteTemplate = useCallback(async (templateId) => {
    try {
      await axios.delete(`${API}/api/speed-templates/${templateId}`, { withCredentials: true });
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      toast.success('Template deleted');
    } catch (e) { toast.error('Failed to delete'); }
  }, []);

  // Persist session history to localStorage
  useEffect(() => {
    localStorage.setItem('writeright_speed_session', JSON.stringify({
      timestamp: Date.now(),
      notes: sessionHistory,
    }));
  }, [sessionHistory]);

  // Add completed panels to session history
  const saveToSessionHistory = useCallback((panelsToSave) => {
    const newEntries = panelsToSave
      .filter(p => p.output)
      .map((p, i) => ({
        id: p.id,
        label: p.label || `Patient`,
        title: generatePanelTitle(p, i, titleFormat),
        notes: p.notes,
        output: p.output,
        icd10: p.icd10 || [],
        age: p.age,
        gender: p.gender,
        status: p.status,
        apptDate: p.apptDate,
        apptTime: p.apptTime,
        savedAt: new Date().toISOString(),
      }));
    if (newEntries.length > 0) {
      setSessionHistory(prev => [...prev, ...newEntries]);
    }
    return newEntries.length;
  }, []);

  // Session timer
  useEffect(() => {
    if (timerPaused) return;
    const t = setInterval(() => {
      setSessionElapsed(Math.floor((Date.now() - sessionStart - pauseOffset.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [sessionStart, timerPaused]);

  const toggleTimerPause = useCallback(() => {
    setTimerPaused(prev => {
      if (!prev) { setPausedAt(Date.now()); }
      else { pauseOffset.current += Date.now() - pausedAt; }
      return !prev;
    });
  }, [pausedAt]);

  const bumpCounter = useCallback(() => {
    setChartsCleared(prev => {
      const next = prev + 1;
      const milestones = [
        { at: 12, icon: Trophy, msg: 'Beast Mode!' },
        { at: 8, icon: Zap, msg: 'On Fire!' },
        { at: 4, icon: Flame, msg: 'Heating Up!' },
      ];
      for (const m of milestones) {
        if (next >= m.at && lastMilestone.current < m.at) {
          setStreakFlash(m);
          lastMilestone.current = m.at;
          setTimeout(() => setStreakFlash(null), 3000);
          break;
        }
      }
      return next;
    });
  }, []);

  const clearPanel = useCallback((idx) => {
    // Save to session history first if panel has output (save-and-clear)
    const panel = panels[idx];
    if (panel.output) {
      saveToSessionHistory([panel]);
      bumpCounter();
      toast.success('Saved to session history & cleared');
    }
    setPanels(prev => prev.map((p, i) => i === idx ? EMPTY_PANEL() : p));
    // Clear the captured audio + cleanup state for this panel index too
    setAudioBlobs(prev => { const next = { ...prev }; delete next[idx]; return next; });
    setAiCleanupBusy(prev => { const next = { ...prev }; delete next[idx]; return next; });
  }, [panels, saveToSessionHistory, bumpCounter]);

  const updatePanel = useCallback((idx, updates) => {
    setPanels(prev => prev.map((p, i) => i === idx ? { ...p, ...updates } : p));
    // Trigger smart ICD-10 detect when notes change
    if (updates.notes !== undefined) {
      triggerIcd10Detect(idx, updates.notes);
      // Feb 27, 2026 (v6.8.19) — modality auto-detect from notes keywords
      // ("vh", "virtual", "video visit", "audio only", "telephone", etc.).
      triggerModalityDetect(idx, updates.notes);
    }
  }, [triggerIcd10Detect, triggerModalityDetect]);

  // Load template into panel (must be after updatePanel)
  const loadTemplate = useCallback((idx, template) => {
    updatePanel(idx, {
      output: template.content,
      icd10: template.icd10_codes || [],
      modality: template.modality || 'in_person',
      label: template.name || '',
    });
    setShowTemplates(false);
    toast.success(`Loaded "${template.name}"`);
  }, [updatePanel]);


  // Feb 27, 2026 (v6.8.18) — Per-panel streaming abort controllers.
  // Stored in a ref (not state) so the Stop button can grab them without
  // forcing a re-render. Key is panel index. Cleared on completion/abort.
  const streamAbortersRef = useRef({});

  const generateNote = useCallback(async (idx) => {
    const panel = panels[idx];
    if (!panel.notes || panel.notes.trim().length < 10) {
      toast.error(`Panel ${idx + 1}: Enter more notes before generating`);
      return;
    }
    // Feb 27, 2026 (v6.8.17) — REGEN-AFTER-EDIT preservation.
    // If this panel already has output AND the provider has manually
    // edited it (output !== outputOriginal), send the edited version as
    // `prior_output_edits`. Backend prompt is hardened to preserve every
    // detail in either notes OR prior_output_edits — fixes the "scant
    // note on regenerate" bug where additions kept getting dropped.
    const _hasOutput = !!(panel.output && panel.output.trim());
    const _editedOutput =
      _hasOutput && panel.outputOriginal && panel.output !== panel.outputOriginal
        ? panel.output
        : null;

    // Feb 27, 2026 (v6.8.18) — STREAMING. Open an SSE stream to
    // /api/generate/stream and paint tokens into panel.output as they
    // arrive. Falls back to non-streaming /api/generate on any error or
    // an early abort by the provider's Stop button. The frontend tracks
    // time-to-first-token (TTFT) for the telemetry pill.
    updatePanel(idx, { isGenerating: true, isStreaming: true, genTime: 0, output: '', ttfMs: 0 });
    const timerStart = Date.now();
    const timerInterval = setInterval(() => {
      setPanels(prev => prev.map((p, i) => i === idx ? { ...p, genTime: Math.floor((Date.now() - timerStart) / 1000) } : p));
    }, 1000);

    // Try streaming first.
    const aborter = new AbortController();
    streamAbortersRef.current[idx] = aborter;
    try {
      const streamRes = await fetch(`${API}/api/generate/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: aborter.signal,
        body: JSON.stringify({
          notes: panel.notes,
          patient_age: panel.age ? parseInt(panel.age) : null,
          patient_gender: panel.gender || null,
          patient_status: panel.status || null,
          generate_types: ['clinical_note'],
          encounter_type: 'established',
          visit_type: panel.visitType || 'in_person_exam',
          include_ros: true,
          include_pe: panel.visitType !== 'virtual' && panel.visitType !== 'results_review_virtual',
          pe_unremarkable: panel.visitType !== 'virtual' && panel.visitType !== 'results_review_virtual',
          guest_id: guestId,
          clinical_tone: clinicalTone || 'neutral',
          icd10_order: panel.icd10.length > 0 ? panel.icd10.map(c => c.code).join(', ') : null,
          ap_format: (typeof localStorage !== 'undefined' && localStorage.getItem('writeright_ap_format')) || 'bulleted',
          prior_output_edits: _editedOutput,
        }),
      });
      if (!streamRes.ok || !streamRes.body) {
        throw new Error(`Stream HTTP ${streamRes.status}`);
      }
      const reader = streamRes.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let accumulated = '';
      let firstTokenSeen = false;
      let lastPaint = 0;
      const PAINT_INTERVAL_MS = 60; // throttle React updates to ~16fps
      // Track if we got at least one token — fallback only if NO tokens.
      let gotAnyToken = false;

      // Read loop. Each `read()` may return multiple SSE events concatenated.
      // We accumulate into `buf`, split on `\n\n`, parse each `data:` line.
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let sep;
        while ((sep = buf.indexOf('\n\n')) !== -1) {
          const evt = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          if (!evt.startsWith('data:')) continue;
          const payload = evt.slice(5).trim();
          if (!payload) continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.type === 'token' && typeof parsed.delta === 'string') {
              if (!firstTokenSeen) {
                firstTokenSeen = true;
                gotAnyToken = true;
                const ttft = Date.now() - timerStart;
                updatePanel(idx, { ttfMs: ttft });
              }
              accumulated += parsed.delta;
              // Throttled paint — batch token updates to avoid hammering React.
              const now = Date.now();
              if (now - lastPaint >= PAINT_INTERVAL_MS) {
                lastPaint = now;
                updatePanel(idx, { output: accumulated });
              }
            } else if (parsed.type === 'final' && typeof parsed.text === 'string') {
              // Feb 28, 2026 — backend post-stream gender-pronoun safety
              // scrub. Replace the locally accumulated buffer with the
              // corrected text so the panel matches the non-streaming
              // /api/generate behavior. Only fired when the scrub actually
              // changed something (see server.py event_stream).
              accumulated = parsed.text;
              updatePanel(idx, { output: accumulated });
            } else if (parsed.type === 'done') {
              // final paint to ensure we don't drop the last <60ms of tokens
              updatePanel(idx, { output: accumulated });
            } else if (parsed.type === 'error') {
              throw new Error(parsed.msg || 'Stream error');
            }
          } catch (parseErr) {
            // Malformed SSE event — log and continue (single bad chunk
            // shouldn't kill the whole stream).
            // eslint-disable-next-line no-console
            console.warn('[WriteRight] SSE parse error:', parseErr, payload);
          }
        }
      }

      // If we got tokens, the stream succeeded — finalize and return.
      if (gotAnyToken) {
        clearInterval(timerInterval);
        delete streamAbortersRef.current[idx];
        updatePanel(idx, {
          output: accumulated,
          outputOriginal: accumulated,
          isGenerating: false,
          isStreaming: false,
          genTime: Math.floor((Date.now() - timerStart) / 1000),
          lastGenMs: Date.now() - timerStart,
        });
        return;
      }
      // No tokens — fall through to non-streaming fallback below.
      throw new Error('Stream returned no tokens');
    } catch (streamErr) {
      // If the provider clicked Stop, don't fall back — just finalize what
      // we have (could be a partial note).
      if (aborter.signal.aborted) {
        clearInterval(timerInterval);
        delete streamAbortersRef.current[idx];
        updatePanel(idx, {
          isGenerating: false,
          isStreaming: false,
          genTime: Math.floor((Date.now() - timerStart) / 1000),
          lastGenMs: Date.now() - timerStart,
        });
        toast.info(`Panel ${idx + 1}: stopped mid-stream`);
        return;
      }
      // Otherwise — log and fall through to the legacy non-streaming path.
      // eslint-disable-next-line no-console
      console.warn('[WriteRight] streaming failed, falling back to /api/generate:', streamErr?.message);
      delete streamAbortersRef.current[idx];
      updatePanel(idx, { isStreaming: false });
    }
    // Slow-connection warning after 15s
    const slowTimeout = setTimeout(() => {
      toast.warning(`Panel ${idx + 1}: connection slow — still trying…`, {
        id: `slow-speed-${idx}`,
        duration: 6000,
      });
    }, 15000);
    try {
      const res = await axios.post(`${API}/api/generate`, {
        notes: panel.notes,
        history_id: panel.historyId || null,
        patient_age: panel.age ? parseInt(panel.age) : null,
        patient_gender: panel.gender || null,
        patient_status: panel.status || null,
        generate_types: ['clinical_note'],
        encounter_type: 'established',
        visit_type: panel.visitType || 'in_person_exam',
        include_ros: true,
        include_pe: panel.visitType !== 'virtual' && panel.visitType !== 'results_review_virtual',
        pe_unremarkable: panel.visitType !== 'virtual' && panel.visitType !== 'results_review_virtual',
        is_patient_mode: false,
        guest_id: guestId,
        clinical_tone: clinicalTone || 'neutral',
        icd10_order: panel.icd10.length > 0 ? panel.icd10.map(c => c.code).join(', ') : null,
        // Honor the provider's saved A/P format preference in Speed Mode too.
        ap_format: (typeof localStorage !== 'undefined' && localStorage.getItem('writeright_ap_format')) || 'bulleted',
        // Feb 27, 2026 (v6.8.17) — preserve manual edits on regenerate.
        prior_output_edits: _editedOutput,
      }, { withCredentials: true, timeout: 120000 });
      clearInterval(timerInterval);
      // Strip markdown bold (**text**) for clean output
      const rawNote = res.data.clinical_note || '';
      let cleanNote = rawNote.replace(/\*\*(.*?)\*\*/g, '$1');
      // Feb 18, 2026 — Speed Mode orders checklist:
      // If the provider explicitly flagged imaging, referrals, or a profile
      // on this panel, prepend a high-visibility "ORDERS TO PLACE" block to
      // the top of the note so they remember to actually place the orders
      // in MHS Genesis (and the profile in MEDPROS) BEFORE closing the
      // chart. This is provider-driven (no LLM auto-detection) so
      // results-review visits don't get confused with new imaging requests.
      const ordersI = (panel.ordersImaging || '').trim();
      const ordersR = (panel.ordersReferrals || '').trim();
      const ordersP = (panel.ordersProfile || '').trim();
      if (ordersI || ordersR || ordersP) {
        const orderLines = [];
        if (ordersI) orderLines.push(`  [ ] IMAGING  (Genesis) → ${ordersI}`);
        if (ordersR) orderLines.push(`  [ ] REFERRAL (Genesis) → ${ordersR}`);
        if (ordersP) orderLines.push(`  [ ] PROFILE  (MEDPROS) → ${ordersP}`);
        // Box-drawing characters survive plain-text paste into Genesis and
        // are visually striking even without color/bold formatting.
        const bar = '\u2550'.repeat(58);  // ═
        const ordersBlock =
          '\u2554' + bar + '\u2557\n' +                              // ╔═...═╗
          '\u2551  >>> ORDERS TO PLACE BEFORE SIGNING THIS CHART <<<       \u2551\n' +
          '\u2560' + bar + '\u2563\n' +                              // ╠═...═╣
          orderLines.map((l) => '\u2551' + l.padEnd(58) + '\u2551').join('\n') + '\n' +
          '\u255A' + bar + '\u255D\n\n';                             // ╚═...═╝
        cleanNote = ordersBlock + cleanNote;
      }
      // Feb 16, 2026 — user reported: "ICD-10 is automatically [adding] uncertain
      // [codes]… I press x on them before I press regenerate, 5 more clicks
      // necessary". The previous behavior overwrote `panel.icd10` with the
      // LLM's suggestion list, clobbering any codes the provider had
      // manually pinned via the + button. Now we LOCK the provider's codes
      // and surface only the LLM's net-new suggestions as a ghost row the
      // provider can tap to add, never auto-include.
      const userCodes = panel.icd10 || [];
      const llmCodes = res.data.icd10_suggestions || [];
      const userCodeSet = new Set(userCodes.map((c) => (c.code || '').toUpperCase()));
      const llmOnlyExtras = userCodes.length > 0
        ? llmCodes.filter((c) => !userCodeSet.has((c.code || '').toUpperCase()))
        : []; // when user pinned nothing, fall through to old behavior below
      const finalIcd10 = userCodes.length > 0 ? userCodes : llmCodes;
      updatePanel(idx, {
        output: cleanNote,
        // Feb 27, 2026 (v6.8.17) — Store the LLM's pristine output so future
        // regenerates can detect manual edits (output !== outputOriginal) and
        // pass them back as `prior_output_edits` to preserve clinical detail.
        outputOriginal: cleanNote,
        icd10: finalIcd10,
        llmSuggestions: llmOnlyExtras,
        historyId: res.data.history_id || panel.historyId,
        isGenerating: false,
        genTime: Math.floor((Date.now() - timerStart) / 1000),
        lastGenMs: Date.now() - timerStart,
      });
      // Auto-save each completed note to session history
      const completedPanel = { ...panel, output: cleanNote, icd10: finalIcd10 };
      saveToSessionHistory([completedPanel]);
    } catch (err) {
      clearInterval(timerInterval);
      clearTimeout(slowTimeout);
      toast.dismiss(`slow-speed-${idx}`);
      updatePanel(idx, { isGenerating: false });
      if (isNetworkError(err)) {
        stashNetworkDraft({
          source: 'speed',
          notes: panel.notes,
          label: panel.label || `Panel ${idx + 1}`,
          meta: {
            age: panel.age,
            gender: panel.gender,
            status: panel.status,
            apptDate: panel.apptDate,
            apptTime: panel.apptTime,
            visitType: panel.visitType,
          },
        });
        toast.error(`Panel ${idx + 1}: connection dropped — saved locally. Restore from banner.`);
      } else {
        toast.error(`Panel ${idx + 1}: Generation failed — ${err.response?.data?.detail || err.message}`);
      }
    }
    clearTimeout(slowTimeout);
    toast.dismiss(`slow-speed-${idx}`);
  }, [panels, guestId, updatePanel, clinicalTone]);

  const generateAll = useCallback(async () => {
    // Feb 27, 2026 (v6.8.17) — Telemetry: fire all eligible panels in parallel
    // via Promise.all so we can measure WALL-CLOCK end-to-end and prove
    // whether the backend is truly parallel-async or secretly serializing.
    // If parallel ✓ → total time ≈ slowest single call.
    // If serial ✗ → total time ≈ sum of all calls.
    const eligible = panels
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => p.notes && p.notes.trim().length >= 10 && !p.isGenerating);
    if (eligible.length === 0) return;
    const _wallStart = Date.now();
    const _panelStarts = eligible.map(() => Date.now());
    // Fire all panels in parallel. generateNote is already async — Promise.all
    // ensures they actually overlap (forEach without await still kicks them
    // off, but Promise.all gives us a deterministic completion point).
    await Promise.all(eligible.map(({ i }, eIdx) => {
      _panelStarts[eIdx] = Date.now();
      return generateNote(i);
    }));
    const _wallMs = Date.now() - _wallStart;
    // Per-panel durations were recorded in generateNote → panel.lastGenMs.
    // Read them from the latest state via setPanels callback for accuracy.
    setPanels(prev => {
      const durations = eligible.map(({ i }) => prev[i]?.lastGenMs || 0).filter(Boolean);
      if (durations.length === 0) return prev;
      const maxDur = Math.max(...durations);
      const sumDur = durations.reduce((a, b) => a + b, 0);
      // Parallelism index: 1.0 = perfectly serial, ~N = perfectly parallel.
      const parallelism = sumDur / Math.max(_wallMs, 1);
      const isTrulyParallel = parallelism > durations.length * 0.6;
      const summary = `Speed Mode batch: ${durations.length} panels · wall=${(_wallMs/1000).toFixed(1)}s · max=${(maxDur/1000).toFixed(1)}s · sum=${(sumDur/1000).toFixed(1)}s · parallelism=${parallelism.toFixed(2)} (${isTrulyParallel ? 'TRULY PARALLEL ✓' : 'SERIAL/QUEUED ✗'})`;
      // eslint-disable-next-line no-console
      console.log('[WriteRight]', summary);
      setLastBatchTelemetry({
        panels: durations.length,
        wallMs: _wallMs,
        maxMs: maxDur,
        sumMs: sumDur,
        parallel: isTrulyParallel,
        finishedAt: Date.now(),
      });
      return prev;
    });
  }, [panels, generateNote]);

  const buildPanelText = useCallback((panel, idx) => {
    const title = generatePanelTitle(panel, idx, titleFormat);
    const header = title ? title + '\n' + '\u2014'.repeat(40) + '\n\n' : '';
    const icdText = panel.icd10.length > 0
      ? 'ICD-10:\n' + panel.icd10.map(c => `${c.code} \u2014 ${c.description}`).join('\n') + '\n\n'
      : '';
    const cpt = SPEED_CPT_OPTIONS.find(o => o.key === panel.modality) || SPEED_CPT_OPTIONS[0];
    const dos = panel.apptDate || new Date().toLocaleDateString();
    const dosTime = toMilitaryTime(panel.apptTime);
    const sig = `\nNote completed | DOS: ${dos} ${dosTime} | CPT ${cpt.code} (${cpt.label})`;
    return header + icdText + panel.output + sig;
  }, [titleFormat]);

  const nextBatch = useCallback(() => {
    // Feb 18, 2026 — Orders-pending guardrail. If any panel has flagged
    // imaging / referrals / profile that the provider hasn't acted on, do
    // NOT silently clear the grid. Pop a confirm dialog so the orders don't
    // get lost before they hit Genesis / MEDPROS.
    const pending = panels.filter(p =>
      (p.ordersImaging || '').trim() || (p.ordersReferrals || '').trim() || (p.ordersProfile || '').trim()
    );
    if (pending.length > 0) {
      const summary = pending.map((p) => {
        const idx = panels.indexOf(p);
        const label = generatePanelTitle(p, idx, titleFormat) || `Patient ${idx + 1}`;
        const bits = [];
        if ((p.ordersImaging || '').trim()) bits.push('imaging');
        if ((p.ordersReferrals || '').trim()) bits.push('referral');
        if ((p.ordersProfile || '').trim()) bits.push('profile');
        return `• ${label}: ${bits.join(' + ')}`;
      }).join('\n');
      const confirmed = window.confirm(
        `Hold up — ${pending.length} panel${pending.length > 1 ? 's' : ''} still ${pending.length > 1 ? 'have' : 'has'} pending orders:\n\n${summary}\n\nDid you place these in MHS Genesis / MEDPROS already?\n\nOK = Yes, clear the grid.\nCancel = No, let me copy them first.`
      );
      if (!confirmed) {
        toast.info('Use the 📋 Orders button to copy the end-of-day checklist first.', { duration: 4000 });
        return;
      }
    }
    const saved = saveToSessionHistory(panels);
    panels.forEach(p => { if (p.output) bumpCounter(); });
    setPanels(Array.from({ length: gridSize }, () => EMPTY_PANEL()));
    setFocusedPanel(null);
    setAudioBlobs({});
    setAiCleanupBusy({});
    if (saved > 0) {
      toast.success(`${saved} note${saved > 1 ? 's' : ''} saved to Session History`);
    }
  }, [gridSize, panels, bumpCounter, saveToSessionHistory, titleFormat]);

  const toggleGridSize = useCallback(() => {
    setGridSize(prev => {
      const next = prev === 4 ? 2 : 4;
      localStorage.setItem('writeright_speed_grid', String(next));
      const current = panels.length;
      if (next > current) {
        setPanels(prev2 => [...prev2, ...Array.from({ length: next - current }, () => EMPTY_PANEL())]);
      } else {
        setPanels(prev2 => prev2.slice(0, next));
      }
      return next;
    });
  }, [panels.length]);

  const copyAllReady = useCallback(() => {
    const readyPanels = panels.filter(p => p.output);
    if (readyPanels.length === 0) { toast.error('No panels have output yet'); return; }
    const combined = readyPanels.map((p) => {
      const idx = panels.indexOf(p);
      return `=== PATIENT ${idx + 1} ===\n${buildPanelText(p, idx)}`;
    }).join('\n\n' + '='.repeat(50) + '\n\n');
    safeCopyToClipboard(combined);
    // Save to session history
    saveToSessionHistory(readyPanels);
    readyPanels.forEach(() => bumpCounter());
    toast.success(`Copied ${readyPanels.length} note${readyPanels.length > 1 ? 's' : ''} to clipboard & saved to session`);
  }, [panels, buildPanelText, bumpCounter, saveToSessionHistory]);

  // Feb 18, 2026 — End-of-day batch shopping list:
  // Collects every panel with pending imaging / referrals / profile and
  // produces a single grouped checklist the provider can paste into a
  // scratchpad and walk through Genesis + MEDPROS in one go.
  const copyAllPendingOrders = useCallback(() => {
    const dated = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    const pendingPanels = panels
      .map((p, i) => ({ p, idx: i }))
      .filter(({ p }) => (p.ordersImaging || '').trim() || (p.ordersReferrals || '').trim() || (p.ordersProfile || '').trim());
    if (pendingPanels.length === 0) {
      toast.info('No pending orders flagged on any panel');
      return;
    }
    const genesisLines = [];
    const medprosLines = [];
    pendingPanels.forEach(({ p, idx }) => {
      const tag = generatePanelTitle(p, idx, titleFormat) || `Patient ${idx + 1}`;
      const oi = (p.ordersImaging || '').trim();
      const or = (p.ordersReferrals || '').trim();
      const op = (p.ordersProfile || '').trim();
      if (oi) genesisLines.push(`  [ ] ${tag}  →  IMAGING:  ${oi}`);
      if (or) genesisLines.push(`  [ ] ${tag}  →  REFERRAL: ${or}`);
      if (op) medprosLines.push(`  [ ] ${tag}  →  ${op}`);
    });
    const bar = '\u2550'.repeat(64);
    const parts = [
      '\u2554' + bar + '\u2557',
      '\u2551  END-OF-DAY ORDERS SHOPPING LIST  (' + dated + ')' + ' '.repeat(Math.max(0, 64 - 38 - dated.length)) + '\u2551',
      '\u255A' + bar + '\u255D',
      '',
    ];
    if (genesisLines.length) {
      parts.push('--- MHS GENESIS (' + genesisLines.length + ' item' + (genesisLines.length === 1 ? '' : 's') + ') ---');
      parts.push(...genesisLines);
      parts.push('');
    }
    if (medprosLines.length) {
      parts.push('--- MEDPROS PROFILES (' + medprosLines.length + ' item' + (medprosLines.length === 1 ? '' : 's') + ') ---');
      parts.push(...medprosLines);
      parts.push('');
    }
    parts.push('Total: ' + (genesisLines.length + medprosLines.length) + ' action item' + (genesisLines.length + medprosLines.length === 1 ? '' : 's') + ' across ' + pendingPanels.length + ' patient' + (pendingPanels.length === 1 ? '' : 's') + '.');
    safeCopyToClipboard(parts.join('\n'));
    toast.success(
      `Copied ${genesisLines.length + medprosLines.length} pending action${genesisLines.length + medprosLines.length === 1 ? '' : 's'} across ${pendingPanels.length} patient${pendingPanels.length === 1 ? '' : 's'}`,
      { duration: 4000 }
    );
  }, [panels, titleFormat]);

  const pendingOrdersCount = panels.filter(p =>
    (p.ordersImaging || '').trim() || (p.ordersReferrals || '').trim() || (p.ordersProfile || '').trim()
  ).length;

  // Load history entry into a panel
  const loadHistoryEntry = useCallback((idx, entry) => {
    updatePanel(idx, {
      notes: entry.original_notes || entry.notes || '',
      age: entry.patient_age ? String(entry.patient_age) : '',
      gender: entry.patient_gender || '',
      label: entry.patient_label || entry.chief_complaint || '',
      output: entry.clinical_note || null,
      icd10: entry.icd10_suggestions || [],
      historyId: entry.id || null,
    });
    setShowHistoryPicker(null);
    toast.success('Loaded into panel');
  }, [updatePanel]);

  // Sorted history: most recent first
  const sortedHistory = (history || [])
    .filter(h => h.notes || h.original_notes || h.clinical_note)
    .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));

  const filledCount = panels.filter(p => p.notes.trim().length >= 10).length;

  // Feb 27, 2026 (v6.8.17) — TYPING-LAG FIX.
  // Per-panel title computation runs an expensive multi-regex pipeline.
  // Previously this fired in the render JSX for EVERY panel on every
  // keystroke (~4× regex pipeline × 30Hz keystroke rate in 2×2 grid).
  // Memoize so the regex only re-runs for the panel whose notes actually
  // changed. Key on the inputs that drive the title — note text + label +
  // titleFormat — anything else is irrelevant to the output.
  const panelTitles = useMemo(
    () => panels.map((p, i) => generatePanelTitle(p, i, titleFormat)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [panels.map(p => `${p.notes}::${p.label}`).join('|'), titleFormat]
  );
  const readyCount = panels.filter(p => p.output).length;

  // --- Session History batch operations ---
  const toggleSessionNoteSelection = useCallback((noteId) => {
    setSelectedSessionNotes(prev => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  }, []);

  const selectAllSessionNotes = useCallback(() => {
    if (selectedSessionNotes.size === sessionHistory.length) {
      setSelectedSessionNotes(new Set());
    } else {
      setSelectedSessionNotes(new Set(sessionHistory.map(n => n.id)));
    }
  }, [sessionHistory, selectedSessionNotes.size]);

  const buildSessionNoteText = useCallback((note) => {
    const icdText = note.icd10?.length > 0
      ? 'ICD-10:\n' + note.icd10.map(c => `${c.code} \u2014 ${c.description}`).join('\n') + '\n\n'
      : '';
    return `${note.title}\n${'—'.repeat(40)}\n\n${icdText}${note.output}`;
  }, []);

  const copySelectedSessionNotes = useCallback(() => {
    const selected = sessionHistory.filter(n => selectedSessionNotes.has(n.id));
    if (selected.length === 0) { toast.error('Select notes to copy'); return; }
    const combined = selected.map((n, i) => {
      return `=== ${n.title || `Note ${i + 1}`} ===\n${buildSessionNoteText(n)}`;
    }).join('\n\n' + '='.repeat(50) + '\n\n');
    safeCopyToClipboard(combined);
    toast.success(`Copied ${selected.length} session note${selected.length > 1 ? 's' : ''}`);
  }, [sessionHistory, selectedSessionNotes, buildSessionNoteText]);

  const copyAllSessionNotes = useCallback(() => {
    if (sessionHistory.length === 0) { toast.error('No session notes yet'); return; }
    const combined = sessionHistory.map((n, i) => {
      return `=== ${n.title || `Note ${i + 1}`} ===\n${buildSessionNoteText(n)}`;
    }).join('\n\n' + '='.repeat(50) + '\n\n');
    safeCopyToClipboard(combined);
    toast.success(`Copied all ${sessionHistory.length} session notes`);
  }, [sessionHistory, buildSessionNoteText]);

  const downloadSessionNotes = useCallback(async (format = 'docx') => {
    const selected = selectedSessionNotes.size > 0
      ? sessionHistory.filter(n => selectedSessionNotes.has(n.id))
      : sessionHistory;
    if (selected.length === 0) { toast.error('No notes to download'); return; }
    
    const combined = selected.map((n, i) => {
      const icdText = n.icd10?.length > 0
        ? 'ICD-10: ' + n.icd10.map(c => `${c.code} — ${c.description}`).join(', ') + '\n\n'
        : '';
      return `${n.title || `Note ${i + 1}`}\n${'—'.repeat(40)}\n\n${icdText}${n.output}`;
    }).join('\n\n' + '═'.repeat(50) + '\n\n');

    try {
      const res = await axios.post(`${API}/api/export/${format}`, {
        content: combined,
        doc_type: 'clinical_note',
        filename: `Speed_Session_${new Date().toISOString().slice(0, 10)}`,
      }, { responseType: 'arraybuffer', withCredentials: true, timeout: 30000 });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Speed_Session_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.length} notes as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error(`Download failed: ${err.message}`);
    }
  }, [sessionHistory, selectedSessionNotes]);

  const clearSessionHistory = useCallback(() => {
    setSessionHistory([]);
    setSelectedSessionNotes(new Set());
    localStorage.removeItem('writeright_speed_session');
    toast.success('Session history cleared');
  }, []);

  const removeSessionNote = useCallback((noteId) => {
    setSessionHistory(prev => prev.filter(n => n.id !== noteId));
    setSelectedSessionNotes(prev => {
      const next = new Set(prev);
      next.delete(noteId);
      return next;
    });
  }, []);

  // --- Pending Speed-Mode network drafts (from failed generates while offline) ---
  const [pendingDrafts, setPendingDrafts] = useState(() =>
    getNetworkDrafts().filter(d => d.source === 'speed')
  );
  useEffect(() => {
    const refresh = () => setPendingDrafts(getNetworkDrafts().filter(d => d.source === 'speed'));
    window.addEventListener('writeright:network-drafts-changed', refresh);
    window.addEventListener('online', refresh);
    return () => {
      window.removeEventListener('writeright:network-drafts-changed', refresh);
      window.removeEventListener('online', refresh);
    };
  }, []);

  const restoreDraftToFirstEmpty = useCallback((draft) => {
    const emptyIdx = panels.findIndex(p => !p.output && !p.notes.trim());
    const targetIdx = emptyIdx >= 0 ? emptyIdx : 0; // fall back to panel 0 if all full
    const meta = draft.meta || {};
    updatePanel(targetIdx, {
      notes: draft.notes || '',
      label: draft.label || '',
      age: meta.age || '',
      gender: meta.gender || '',
      status: meta.status || 'active_duty',
      apptDate: meta.apptDate || '',
      apptTime: meta.apptTime || '',
      visitType: meta.visitType || 'in_person_exam',
      output: null,
      icd10: [],
    });
    removeNetworkDraft(draft.id);
    toast.success(`Restored to Panel ${targetIdx + 1} — tap Generate when ready`);
  }, [panels, updatePanel]);

  return (
      <div className="w-full flex-1 bg-background flex flex-col" data-testid="batch-charting-grid">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card shrink-0">
          <div className="flex items-center gap-3">
            <Grid2x2 className="w-5 h-5 text-primary" />
            <h1 className="text-sm font-bold text-foreground">Speed Chart</h1>
            <span className="text-xs text-muted-foreground">({readyCount} ready)</span>
            {/* Grid size toggle */}
            <div className="flex items-center bg-secondary/50 rounded-lg p-0.5 border border-border">
              <button
                onClick={() => { if (gridSize !== 2) toggleGridSize(); }}
                className={`px-2 py-1 rounded text-[10px] font-bold transition-all ${
                  gridSize === 2 ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="grid-size-2"
              >1x2</button>
              <button
                onClick={() => { if (gridSize !== 4) toggleGridSize(); }}
                className={`px-2 py-1 rounded text-[10px] font-bold transition-all ${
                  gridSize === 4 ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="grid-size-4"
              >2x2</button>
            </div>
            {/* Title format selector — controls the copy header. Feb 16, 2026. */}
            <select
              value={titleFormat}
              onChange={(e) => setTitleFormat(e.target.value)}
              className="px-2 py-1 rounded-md text-[10px] font-semibold bg-secondary/50 border border-border text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 cursor-pointer"
              data-testid="speed-title-format-select"
              title="Title format for copied notes"
            >
              {TITLE_FORMAT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          {/* Counter */}
          <div className="flex items-center gap-4" data-testid="batch-counter">
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/50 border border-border">
              {chartsCleared > 0 && (
                <Flame className={`w-4 h-4 ${chartsCleared >= 12 ? 'text-red-500' : chartsCleared >= 4 ? 'text-orange-500' : 'text-amber-500'}`} />
              )}
              <span className="text-sm font-bold text-foreground" data-testid="charts-cleared-count">{chartsCleared}</span>
              <span className="text-xs text-muted-foreground">cleared</span>
              <span className="text-xs text-muted-foreground/50 mx-1">|</span>
              <button
                onClick={toggleTimerPause}
                className={`text-xs font-mono px-1.5 py-0.5 rounded transition-colors cursor-pointer ${
                  timerPaused ? 'text-amber-400 bg-amber-500/15 animate-pulse' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                }`}
                data-testid="session-timer"
              >
                {timerPaused && <span className="mr-1">||</span>}
                {Math.floor(sessionElapsed / 60)}:{String(sessionElapsed % 60).padStart(2, '0')}
              </button>
              {/* Soft pace indicator (Feb 22, 2026) — only appears after 2 min &
                  at least 1 note, so early-session noise doesn't shame anyone.
                  Target = 6/hr (per therapist's plan-of-action). Green dot when
                  on pace, muted dot when not. No alarms, no red. */}
              {sessionElapsed >= 120 && chartsCleared >= 1 && !timerPaused && (() => {
                const perHour = (chartsCleared / sessionElapsed) * 3600;
                const onPace = perHour >= 6;
                return (
                  <>
                    <span className="text-xs text-muted-foreground/50 mx-1">·</span>
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-mono tabular-nums ${onPace ? 'text-emerald-300' : 'text-muted-foreground'}`}
                      title={onPace
                        ? `On pace (target 6/hr). You're moving — keep going.`
                        : `Currently ~${perHour.toFixed(1)}/hr. Target is 6/hr. No shame — just info.`}
                      data-testid="session-pace"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${onPace ? 'bg-emerald-400' : 'bg-muted-foreground/40'}`} />
                      {perHour < 100 ? perHour.toFixed(1) : Math.round(perHour)}/hr
                    </span>
                  </>
                );
              })()}
            </div>
            {streakFlash && (
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full animate-pulse" style={{ background: 'linear-gradient(90deg, rgba(255,215,0,0.2), rgba(255,69,0,0.2))' }}>
                <streakFlash.icon className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-bold text-amber-300">{streakFlash.msg}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Focus Mode toggle — dims non-active panels for visual focus */}
            <button
              onClick={() => {
                setFocusModeEnabled(prev => {
                  const next = !prev;
                  if (!next) setFocusedPanel(null);  // clear focus when turning off
                  return next;
                });
              }}
              className={`px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1 border ${
                focusModeEnabled
                  ? 'border-purple-500/40 bg-purple-500/15 text-purple-300'
                  : 'border-border bg-secondary/50 text-muted-foreground hover:text-foreground'
              }`}
              title={focusModeEnabled ? 'Focus Mode ON — non-active panels dim' : 'Focus Mode OFF — all panels at full brightness'}
              data-testid="focus-mode-toggle"
            >
              <Eye className="w-3 h-3" />
              Focus
            </button>
            {/* Smart ICD-10 toggle */}
            <button
              onClick={() => setSmartIcd10Enabled(prev => !prev)}
              className={`px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1 border ${
                smartIcd10Enabled
                  ? 'border-primary/40 bg-primary/15 text-primary'
                  : 'border-border bg-secondary/50 text-muted-foreground hover:text-foreground'
              }`}
              title={smartIcd10Enabled ? 'Smart ICD-10 Auto-Detect ON' : 'Smart ICD-10 Auto-Detect OFF'}
              data-testid="smart-icd10-toggle"
            >
              <Search className="w-3 h-3" />
              ICD
            </button>
            {/* Batch Recording Mode (Feb 26, 2026) — applies to AI Cleanup on
                every panel in this batch. Shares localStorage with the main
                provider dictation so the choice survives between modes. */}
            <div
              className="flex items-center gap-0.5 px-1.5 py-1 rounded-lg border border-blue-500/30 bg-blue-500/5"
              data-testid="batch-recording-mode-group"
              data-mode={batchRecordingMode}
              title="Recording mode for AI Cleanup transcription in this batch"
            >
              <Mic className="w-3 h-3 text-blue-400/80 mr-0.5" />
              {[
                { v: 'solo', label: 'Solo' },
                { v: 'in_person', label: 'In-Room' },
                { v: 'phone', label: 'Phone' },
              ].map((m) => {
                const active = batchRecordingMode === m.v;
                return (
                  <button
                    key={m.v}
                    type="button"
                    onClick={() => setBatchRecordingMode(m.v)}
                    className={`text-[10px] px-1.5 py-0.5 rounded font-semibold transition-colors ${
                      active
                        ? 'bg-blue-500/30 text-blue-200'
                        : 'text-blue-300/70 hover:bg-blue-500/15 hover:text-blue-200'
                    }`}
                    data-testid={`batch-recording-mode-${m.v}`}
                    title={
                      m.v === 'solo' ? 'Single dictation (no diarization)'
                      : m.v === 'in_person' ? 'In-room two-speaker — speaker labels'
                      : 'Phone call — Provider/Patient turn labels'
                    }
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            {/* Templates */}
            <button
              onClick={() => setShowTemplates(prev => !prev)}
              className={`px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1 border ${
                showTemplates
                  ? 'border-amber-500/40 bg-amber-500/15 text-amber-400'
                  : 'border-border bg-secondary/50 text-muted-foreground hover:text-foreground'
              }`}
              title="Quick Copy Templates"
              data-testid="templates-toggle"
            >
              <Bookmark className="w-3 h-3" />
              {templates.length > 0 ? templates.length : ''}
            </button>
            {readyCount > 0 && (
              <button
                onClick={copyAllReady}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                data-testid="copy-all-ready-btn"
              >
                <Copy className="w-3.5 h-3.5" />
                Copy {readyCount}
              </button>
            )}
            {pendingOrdersCount > 0 && (
              <button
                onClick={copyAllPendingOrders}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 border border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                title="Copy a single end-of-day checklist of every flagged imaging / referral / profile — paste into a scratch pad and walk through Genesis + MEDPROS at once."
                data-testid="copy-all-pending-orders-btn"
              >
                <span className="text-sm leading-none">📋</span>
                Orders ({pendingOrdersCount})
              </button>
            )}
            <button
              onClick={nextBatch}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-secondary hover:bg-secondary/80 text-foreground transition-colors flex items-center gap-1.5 border border-border"
              data-testid="next-batch-btn"
            >
              <SkipForward className="w-3.5 h-3.5" />
              Next {gridSize}
            </button>
            <button
              onClick={generateAll}
              disabled={filledCount === 0}
              className="px-4 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-40"
              style={{ background: 'linear-gradient(180deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)', color: '#1a0a00' }}
              data-testid="generate-all-btn"
            >
              Generate All ({filledCount})
            </button>
            {/* Feb 27, 2026 (v6.8.24) — EXIT Speed Mode button.
                Provider feedback: "there's no exit from speed mode, I have
                to enter the role selector to get out." Wires the existing
                onClose prop to a visible button at the right edge of the
                toolbar so it's discoverable without leaving the page. */}
            {onClose && (
              <button
                onClick={onClose}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border bg-card hover:bg-secondary text-foreground transition-colors flex items-center gap-1.5 ml-auto shrink-0"
                data-testid="exit-speed-mode-btn"
                title="Exit Speed Mode and return to single-patient view"
              >
                <X className="w-3.5 h-3.5" />
                Exit Speed
              </button>
            )}
            {/* Feb 27, 2026 (v6.8.17) — Speed Mode batch telemetry pill.
                After each Generate All run, surface wall-clock + parallelism
                so the provider can see whether the LLM batch was truly
                parallel (≈ slowest single call) or secretly serialized
                (≈ sum of all calls). Hover for full breakdown. Auto-hides
                10 seconds after the batch completes. */}
            {lastBatchTelemetry && (Date.now() - lastBatchTelemetry.finishedAt < 30000) && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold border ${
                  lastBatchTelemetry.parallel
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/50'
                    : 'bg-red-500/15 text-red-300 border-red-400/50'
                }`}
                title={`Speed Mode batch:\n${lastBatchTelemetry.panels} panels\nwall=${(lastBatchTelemetry.wallMs/1000).toFixed(1)}s\nslowest single=${(lastBatchTelemetry.maxMs/1000).toFixed(1)}s\nsum of all=${(lastBatchTelemetry.sumMs/1000).toFixed(1)}s\nParallelism: ${(lastBatchTelemetry.sumMs/Math.max(lastBatchTelemetry.wallMs,1)).toFixed(2)}× (>${(lastBatchTelemetry.panels*0.6).toFixed(1)} = truly parallel)\n\nIf this says SERIAL, the backend is queueing requests (uvicorn workers, asyncio lock, or LLM rate limit). Tell the dev — fixable.`}
                data-testid="speed-batch-telemetry"
              >
                <span className="text-[10px]">🕐</span>
                {(lastBatchTelemetry.wallMs / 1000).toFixed(1)}s
                <span className="opacity-70">·</span>
                {lastBatchTelemetry.parallel ? 'parallel ✓' : 'serial ✗'}
              </span>
            )}
            <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-secondary hover:bg-secondary/80 text-foreground transition-colors flex items-center gap-1.5 border border-border" data-testid="close-batch-charting">
              <X className="w-3.5 h-3.5" />
              Exit
            </button>
          </div>
        </div>

        {/* Templates dropdown */}
        {showTemplates && (
          <div className="shrink-0 border-b border-border bg-card/80 px-4 py-2 max-h-[180px] overflow-y-auto custom-scrollbar" data-testid="templates-panel">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-amber-400 uppercase tracking-wider">Quick Templates</p>
              <button onClick={() => setShowTemplates(false)} className="p-0.5 text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
            </div>
            {templates.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2 text-center">No templates saved yet. Generate a note and click the bookmark icon to save.</p>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-1.5">
                {templates.map((tmpl, ti) => (
                  <div key={tmpl.id || ti} className="group flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-border hover:border-amber-500/30 hover:bg-amber-500/5 transition-all cursor-pointer" data-testid={`template-item-${ti}`}>
                    <button
                      onClick={() => {
                        // Load into first empty panel
                        const emptyIdx = panels.findIndex(p => !p.output && !p.notes.trim());
                        if (emptyIdx >= 0) loadTemplate(emptyIdx, tmpl);
                        else { toast.info('All panels have content. Clear one first.'); }
                      }}
                      className="flex-1 min-w-0 text-left"
                    >
                      <p className="text-[10px] font-semibold text-foreground truncate">{tmpl.name}</p>
                      <div className="flex items-center gap-1">
                        {tmpl.icd10_codes?.slice(0, 3).map((c, ci) => (
                          <span key={ci} className="text-[8px] font-mono text-primary/60">{c.code || c}</span>
                        ))}
                        {tmpl.cpt_code && <span className="text-[8px] font-mono text-muted-foreground">CPT {tmpl.cpt_code}</span>}
                      </div>
                    </button>
                    <button
                      onClick={() => deleteTemplate(tmpl.id)}
                      className="p-0.5 opacity-0 group-hover:opacity-100 text-destructive/50 hover:text-destructive transition-opacity"
                      data-testid={`template-delete-${ti}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pending Speed-Mode Drafts — one-tap restore (from failed offline generates) */}
        {pendingDrafts.length > 0 && (
          <div
            className="shrink-0 border-b border-amber-500/40 bg-amber-500/10 px-3 py-2"
            data-testid="speed-pending-drafts-bar"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span className="text-[11px] font-bold text-amber-300 uppercase tracking-wider">
                {pendingDrafts.length} dropped draft{pendingDrafts.length > 1 ? 's' : ''}
              </span>
              <span className="text-[10px] text-amber-200/70">
                Tap to restore into the next empty panel
              </span>
              <div className="flex items-center gap-1 flex-wrap ml-1">
                {pendingDrafts.slice(0, 6).map((d) => {
                  const preview = (d.notes || '').trim().slice(0, 28);
                  return (
                    <div
                      key={d.id}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/40 hover:bg-amber-500/25 transition-colors"
                    >
                      <button
                        onClick={() => restoreDraftToFirstEmpty(d)}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-200"
                        data-testid={`speed-draft-restore-${d.id}`}
                        title={`Restore: ${d.notes?.slice(0, 120) || ''}`}
                      >
                        <RotateCcw className="w-2.5 h-2.5" />
                        {d.label || 'Draft'}: "{preview}{d.notes && d.notes.length > 28 ? '…' : ''}"
                      </button>
                      <button
                        onClick={() => { removeNetworkDraft(d.id); toast.info('Draft discarded'); }}
                        className="p-0.5 text-amber-300/60 hover:text-amber-200 rounded"
                        title="Discard this draft"
                        data-testid={`speed-draft-discard-${d.id}`}
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  );
                })}
                {pendingDrafts.length > 6 && (
                  <span className="text-[10px] text-amber-300/70">+{pendingDrafts.length - 6} more</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Grid — always side-by-side for wider cards */}
        <div className={`flex-1 grid gap-px bg-border overflow-hidden ${
          gridSize === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'
        }`} style={{ gridTemplateRows: gridSize <= 2 ? '1fr' : '1fr 1fr' }}>
          {panels.map((panel, idx) => {
            // Focus dimming: when focus mode is on AND the user is actively
            // working in a specific panel, fade the others. Recording overrides
            // (the recording panel is always lit so you can see live transcription).
            const isDimmed = focusModeEnabled
              && focusedPanel !== null
              && focusedPanel !== idx
              && recordingPanel !== idx;
            // Feb 27, 2026 (v6.8.17) — Active/NEXT panel indicator.
            // Always-on visual cue (independent of Focus Mode) so the
            // provider knows which box is the current target.
            const isActive = activePanelIdx === idx;
            return (
            <div
              key={panel.id}
              className={`relative bg-card flex flex-col overflow-hidden transition-all duration-300 ${
                isDimmed ? 'opacity-50' : 'opacity-100'
              } ${
                isActive
                  ? 'ring-2 ring-emerald-400/80 ring-offset-1 ring-offset-background shadow-lg shadow-emerald-500/20 z-10'
                  : 'ring-1 ring-border/30'
              }`}
              onClick={() => { focusModeEnabled && setFocusedPanel(idx); setActivePanelIdx(idx); }}
              onFocusCapture={() => { focusModeEnabled && setFocusedPanel(idx); setActivePanelIdx(idx); }}
              data-testid={`batch-panel-${idx}`}
              data-focused={focusedPanel === idx ? 'true' : 'false'}
              data-active={isActive ? 'true' : 'false'}
            >
              {isActive && (
                <span
                  className="absolute -top-2 left-2 z-20 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500 text-emerald-50 text-[9px] font-bold uppercase tracking-wider shadow shadow-emerald-700/40 pointer-events-none"
                  data-testid={`batch-active-pill-${idx}`}
                >
                  <ChevronRight className="w-2.5 h-2.5" />
                  Active
                </span>
              )}
              {/* Panel header — compact layout */}
              <div className="px-2 py-1 border-b border-border/50 bg-secondary/30 shrink-0 space-y-0.5">
                {/* Row 1: Label + action buttons */}
                <div className="flex items-center gap-1.5">
                  <HourglassIcon className="w-3 h-3 shrink-0" />
                  <input type="text" value={panel.label} onChange={(e) => updatePanel(idx, { label: e.target.value })}
                    placeholder={`Patient ${idx + 1}`}
                    className="flex-1 min-w-0 bg-transparent text-xs font-semibold text-foreground placeholder:text-muted-foreground/50 outline-none"
                    data-testid={`batch-label-${idx}`}
                  />
                  {/* Load from History button */}
                  <button
                    onClick={() => setShowHistoryPicker(showHistoryPicker === idx ? null : idx)}
                    className={`p-1 rounded transition-colors shrink-0 ${showHistoryPicker === idx ? 'bg-primary/20 text-primary' : 'hover:bg-secondary text-muted-foreground hover:text-foreground'}`}
                    title="Load from history / drafts"
                    data-testid={`batch-load-history-${idx}`}
                  >
                    <Clock className="w-3.5 h-3.5" />
                  </button>
                  {/* Mic button for voice dictation */}
                  <button
                    onClick={() => recordingPanel === idx ? stopDictation() : startDictation(idx)}
                    className={`p-1 rounded transition-colors shrink-0 ${
                      recordingPanel === idx
                        ? 'bg-red-500/20 text-red-400 animate-pulse'
                        : 'hover:bg-secondary text-muted-foreground hover:text-foreground'
                    }`}
                    title={recordingPanel === idx ? 'Stop dictation' : 'Voice dictation'}
                    data-testid={`batch-mic-${idx}`}
                  >
                    {recordingPanel === idx ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                  </button>
                  {/* Feb 27, 2026 (v6.8.20) — Per-panel "→ Next" button.
                      Provider asked: "is there a next button for each box?"
                      Advances the ACTIVE pill to the next panel AND auto-
                      focuses its notes textarea (with smooth scroll if it's
                      off-screen on small viewports). Wraps to panel 0 from
                      the last panel. Visible on every panel. */}
                  <button
                    onClick={() => {
                      const nextIdx = (idx + 1) % panels.length;
                      setActivePanelIdx(nextIdx);
                      // Defer focus to after React re-renders so the textarea exists.
                      setTimeout(() => {
                        const nextTa = document.querySelector(`[data-testid="batch-notes-${nextIdx}"]`);
                        if (nextTa) {
                          nextTa.focus();
                          nextTa.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 30);
                    }}
                    className="p-1 rounded transition-colors shrink-0 hover:bg-emerald-500/15 text-emerald-400 hover:text-emerald-300 group"
                    title={`Jump to panel ${((idx + 1) % panels.length) + 1} and focus its notes`}
                    data-testid={`batch-next-panel-${idx}`}
                    aria-label="Next panel"
                  >
                    <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                  {/* AI Cleanup — re-transcribe the captured audio with Deepgram Nova-3
                      (replaces the panel notes with the medical-grade transcript). Only
                      enabled after a dictation has finished AND we have an audio blob. */}
                  {audioBlobs[idx] && recordingPanel !== idx && (
                    <button
                      onClick={() => aiCleanupPanel(idx)}
                      disabled={aiCleanupBusy[idx]}
                      className={`p-1 rounded transition-colors shrink-0 ${
                        aiCleanupBusy[idx]
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'hover:bg-emerald-500/15 text-emerald-400 hover:text-emerald-300'
                      }`}
                      title="AI Cleanup — re-transcribe the last dictation with Deepgram Nova-3 (replaces panel notes)"
                      data-testid={`batch-ai-cleanup-${idx}`}
                    >
                      {aiCleanupBusy[idx]
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <Sparkles className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  <button onClick={() => clearPanel(idx)}
                    className="p-1 hover:bg-destructive/20 rounded text-muted-foreground hover:text-destructive transition-colors shrink-0"
                    title="Clear this panel"
                    data-testid={`batch-clear-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {/* Row 2: Date/Time/Age/Gender — Feb 27, 2026 (v6.8.24)
                    flex-wrap added so demographic inputs stay visible on
                    narrow / portrait viewports (provider feedback: "in
                    the 1×2 speed mode you can't see the date and age
                    fields at desktop portrait"). Without flex-wrap they
                    were clipping behind the action buttons. */}
                <div className="flex items-center gap-1 flex-wrap">
                  <input type="date" value={panel.apptDate} onChange={(e) => updatePanel(idx, { apptDate: e.target.value })}
                    className="bg-input border border-border rounded px-1 py-0.5 text-[10px] text-foreground w-[105px] shrink-0"
                    data-testid={`batch-date-${idx}`}
                  />
                  <input type="time" value={panel.apptTime} onChange={(e) => updatePanel(idx, { apptTime: e.target.value })}
                    className="bg-input border border-border rounded px-1 py-0.5 text-[10px] text-foreground w-[75px] shrink-0"
                    data-testid={`batch-time-${idx}`}
                  />
                  <input type="number" value={panel.age} onChange={(e) => updatePanel(idx, { age: e.target.value })}
                    placeholder="Age" className="w-12 bg-input border border-border rounded px-1.5 py-0.5 text-[10px] text-foreground placeholder:text-muted-foreground/50 shrink-0"
                    data-testid={`batch-age-${idx}`}
                  />
                  {/* Feb 27, 2026 (v6.8.24) — Added "X" (they/them) option
                      per provider request: a gender control that auto-
                      corrects pronouns. M→he/him, F→she/her, X→they/them.
                      The backend already honors patient_gender for
                      pronoun selection. */}
                  <select value={panel.gender} onChange={(e) => updatePanel(idx, { gender: e.target.value })}
                    className="bg-input border border-border rounded px-1 py-0.5 text-[10px] text-foreground shrink-0"
                    data-testid={`batch-gender-${idx}`}
                    title="Sets pronoun: M→he/him, F→she/her, X→they/them"
                  >
                    <option value="">M/F/X</option>
                    <option value="male">M</option>
                    <option value="female">F</option>
                    <option value="other">X</option>
                  </select>
                  {/* Visit type toggle — Feb 27, 2026 (v6.8.19) couples
                      visitType AND modality so the CPT code at the bottom
                      of the panel reflects reality. Previously clicking VH
                      only flipped visitType, leaving modality='in_person'
                      and the CPT stuck at 99214. Now VH bumps modality to
                      'video' (the most common virtual encounter); the
                      bottom IP/Vid/Aud toggle still lets the provider
                      switch to audio-only afterwards. */}
                  <div className="flex rounded border border-border overflow-hidden ml-auto shrink-0">
                    <button
                      onClick={() => updatePanel(idx, { visitType: 'in_person_exam', modality: 'in_person', modalityManuallySet: true })}
                      className={`px-1.5 py-0.5 text-[9px] font-semibold transition-colors ${panel.visitType !== 'virtual' && panel.visitType !== 'results_review_virtual' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid={`batch-in-person-${idx}`}
                    >IP</button>
                    <button
                      onClick={() => updatePanel(idx, { visitType: 'virtual', modality: panel.modality === 'audio' ? 'audio' : 'video', modalityManuallySet: true })}
                      className={`px-1.5 py-0.5 text-[9px] font-semibold transition-colors ${panel.visitType === 'virtual' || panel.visitType === 'results_review_virtual' ? 'bg-blue-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid={`batch-virtual-${idx}`}
                    >VH</button>
                  </div>
                </div>
              </div>

              {/* History Picker dropdown */}
              {showHistoryPicker === idx && (
                <div className="shrink-0 max-h-[200px] overflow-y-auto border-b border-primary/30 bg-primary/5 p-2 space-y-1 custom-scrollbar" data-testid={`history-picker-${idx}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[10px] font-bold text-primary uppercase tracking-wider">Load from History</p>
                    <button onClick={() => setShowHistoryPicker(null)} className="text-muted-foreground hover:text-foreground p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                  {sortedHistory.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2 text-center">No history entries yet</p>
                  ) : (
                    sortedHistory.slice(0, 30).map((entry, hi) => (
                      <button
                        key={entry.id || hi}
                        onClick={() => loadHistoryEntry(idx, entry)}
                        className="w-full text-left px-2 py-1.5 rounded-md hover:bg-primary/10 transition-colors flex items-center gap-2"
                        data-testid={`history-pick-${idx}-${hi}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] font-bold text-primary tabular-nums shrink-0">{formatHistoryTime(entry.timestamp, entry.patient_label)}</span>
                            <p className="text-xs font-medium text-foreground truncate">
                              {getHistoryDisplayTitle(entry)}
                            </p>
                            {entry.is_draft && <Badge variant="outline" className="text-[8px] border-amber-500/40 text-amber-400 shrink-0 h-4 px-1">Draft</Badge>}
                          </div>
                          <p className="text-[10px] text-muted-foreground">
                            {entry.patient_age ? `${entry.patient_age}yo` : ''}{entry.patient_gender ? ` ${entry.patient_gender === 'male' ? 'M' : 'F'}` : ''}
                            {entry.timestamp ? ` \u00B7 ${new Date(entry.timestamp).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                        <FileText className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* Content area */}
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden relative">
                {/* Generation overlay */}
                {panel.isGenerating && (
                  <div className="absolute inset-0 z-10 bg-background/60 backdrop-blur-sm flex flex-col items-center justify-center gap-2" data-testid={`batch-generating-overlay-${idx}`}>
                    <div className="relative">
                      <Loader2 className="w-8 h-8 text-primary animate-spin" />
                      <HourglassIcon className="w-4 h-4 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                    </div>
                    <span className="text-xs font-semibold text-primary">{panel.genTime || 0}s</span>
                    <span className="text-[10px] text-muted-foreground">Generating...</span>
                  </div>
                )}
                {!panel.output ? (
                  <div className="flex-1 flex flex-col p-2 gap-1 min-h-0">
                    {/* Title + generate button row */}
                    {panel.notes.trim().length >= 10 && (
                      <div className="shrink-0 flex items-center justify-between gap-1">
                        <p className="text-[10px] text-amber-400/80 truncate flex-1" data-testid={`batch-live-title-${idx}`}>
                          {panelTitles[idx]}
                        </p>
                        <button
                          onClick={() => generateNote(idx)}
                          disabled={panel.isGenerating || panel.notes.trim().length < 10}
                          className="shrink-0 p-1.5 rounded-lg transition-all disabled:opacity-40"
                          style={{
                            background: panel.isGenerating
                              ? 'linear-gradient(180deg, #333 0%, #222 100%)'
                              : 'linear-gradient(180deg, #FFD700 0%, #DAA520 50%, #B8860B 100%)',
                            color: panel.isGenerating ? '#daa520' : '#1a0a00',
                          }}
                          title={panel.isGenerating ? `Crafting... ${panel.genTime}s` : 'Generate Note'}
                          data-testid={`batch-generate-${idx}`}
                        >
                          <HourglassIcon className={`w-4 h-4 ${panel.isGenerating ? 'animate-pulse' : ''}`} />
                        </button>
                      </div>
                    )}
                    {/* Pre-generation ICD-10 chips (from smart auto-detect) */}
                    {panel.icd10.length > 0 && !panel.output && (
                      <div className="shrink-0 flex items-center gap-1 flex-wrap" data-testid={`batch-pre-icd10-${idx}`}>
                        {detectingIcd10[idx] && <Loader2 className="w-2.5 h-2.5 text-primary animate-spin shrink-0" />}
                        {panel.icd10.map((code, ci) => (
                          <span
                            key={ci}
                            className="inline-flex items-center gap-0.5 text-[8px] font-mono font-semibold bg-primary/10 text-primary/80 px-1 py-0.5 rounded border border-primary/20 cursor-help"
                            title={`${code.code}: ${code.description}`}
                            data-testid={`batch-pre-icd10-chip-${idx}-${ci}`}
                          >
                            {code.code}
                            <button
                              onClick={() => {
                                const updated = [...panel.icd10];
                                updated.splice(ci, 1);
                                updatePanel(idx, { icd10: updated });
                              }}
                              className="ml-0.5 text-destructive/60 hover:text-destructive"
                            >
                              <X className="w-2 h-2" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Recording indicator */}
                    {recordingPanel === idx && (
                      <div className="shrink-0 flex items-center gap-1.5 px-2 py-1 rounded bg-red-500/10 border border-red-500/20">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-[9px] text-red-400 font-medium">Listening...</span>
                      </div>
                    )}
                    {/* Feb 27, 2026 (v6.8.23) — Live interim transcript.
                        Browser SpeechRecognition holds "final" until the
                        speaker pauses 1-3s, so without this strip the
                        textarea looked dead while you were mid-sentence.
                        Faint italic, read-only — it merges into panel.notes
                        as soon as the recognizer finalizes the chunk. */}
                    {recordingPanel === idx && panel.interimText && (
                      <div
                        className="shrink-0 px-2 py-1 rounded bg-blue-500/5 border border-blue-500/30 text-[10px] italic text-blue-300/85 max-h-[60px] overflow-y-auto"
                        data-testid={`batch-interim-${idx}`}
                      >
                        <span className="text-[8px] uppercase font-bold tracking-wider text-blue-400/60 mr-1">speaking:</span>
                        {panel.interimText}
                      </div>
                    )}
                    {/* Orders to place — provider checkbox-style toggles.
                        Prepended to the generated note as a "PLACE BEFORE
                        SIGNING" reminder so imaging / referrals don't get
                        forgotten on the way out of the chart. Feb 18, 2026. */}
                    <div className="shrink-0 flex flex-col gap-1" data-testid={`batch-orders-row-${idx}`}>
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-[9px] text-muted-foreground/80 uppercase tracking-wider mr-0.5">Orders to place:</span>
                        {!panel.ordersImaging && (
                          <button
                            type="button"
                            onClick={() => updatePanel(idx, { ordersImaging: ' ' })}
                            className="px-1.5 py-0.5 text-[9px] font-semibold rounded border border-border bg-secondary/30 text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-primary/10 transition-colors"
                            title="Add an imaging order reminder to this note"
                            data-testid={`batch-add-imaging-${idx}`}
                          >
                            + Imaging
                          </button>
                        )}
                        {!panel.ordersReferrals && (
                          <button
                            type="button"
                            onClick={() => updatePanel(idx, { ordersReferrals: ' ' })}
                            className="px-1.5 py-0.5 text-[9px] font-semibold rounded border border-border bg-secondary/30 text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-primary/10 transition-colors"
                            title="Add a referral reminder to this note"
                            data-testid={`batch-add-referral-${idx}`}
                          >
                            + Referral
                          </button>
                        )}
                        {!panel.ordersProfile && (
                          <button
                            type="button"
                            onClick={() => updatePanel(idx, { ordersProfile: ' ' })}
                            className="px-1.5 py-0.5 text-[9px] font-semibold rounded border border-border bg-secondary/30 text-muted-foreground hover:text-foreground hover:border-amber-400/50 hover:bg-amber-500/10 transition-colors"
                            title="Add a MEDPROS profile reminder to this note"
                            data-testid={`batch-add-profile-${idx}`}
                          >
                            + Profile
                          </button>
                        )}
                      </div>
                      {panel.ordersImaging !== '' && (
                        <div className="flex items-center gap-1">
                          <span className="text-[9px] font-bold text-emerald-400 shrink-0 w-[58px]">Imaging:</span>
                          <input
                            type="text"
                            value={panel.ordersImaging}
                            onChange={(e) => updatePanel(idx, { ordersImaging: e.target.value })}
                            placeholder="e.g. MRI L knee w/o contrast"
                            className="flex-1 h-6 px-1.5 bg-input border border-emerald-500/30 rounded text-[10px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
                            data-testid={`batch-orders-imaging-${idx}`}
                            autoFocus={panel.ordersImaging === ' '}
                          />
                          <button
                            type="button"
                            onClick={() => updatePanel(idx, { ordersImaging: '' })}
                            className="text-muted-foreground/60 hover:text-destructive shrink-0 p-0.5"
                            title="Remove imaging reminder"
                            data-testid={`batch-clear-imaging-${idx}`}
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      )}
                      {panel.ordersReferrals !== '' && (
                        <div className="flex items-center gap-1">
                          <span className="text-[9px] font-bold text-emerald-400 shrink-0 w-[58px]">Referrals:</span>
                          <input
                            type="text"
                            value={panel.ordersReferrals}
                            onChange={(e) => updatePanel(idx, { ordersReferrals: e.target.value })}
                            placeholder="e.g. Ortho, PT (knee)"
                            className="flex-1 h-6 px-1.5 bg-input border border-emerald-500/30 rounded text-[10px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
                            data-testid={`batch-orders-referrals-${idx}`}
                            autoFocus={panel.ordersReferrals === ' '}
                          />
                          <button
                            type="button"
                            onClick={() => updatePanel(idx, { ordersReferrals: '' })}
                            className="text-muted-foreground/60 hover:text-destructive shrink-0 p-0.5"
                            title="Remove referral reminder"
                            data-testid={`batch-clear-referrals-${idx}`}
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      )}
                      {panel.ordersProfile !== '' && (
                        <div className="flex items-center gap-1">
                          <span className="text-[9px] font-bold text-amber-400 shrink-0 w-[58px]">Profile:</span>
                          <input
                            type="text"
                            value={panel.ordersProfile}
                            onChange={(e) => updatePanel(idx, { ordersProfile: e.target.value })}
                            placeholder="e.g. Temp P3 knee, 60 days  (logged in MEDPROS)"
                            className="flex-1 h-6 px-1.5 bg-input border border-amber-500/40 rounded text-[10px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                            data-testid={`batch-orders-profile-${idx}`}
                            autoFocus={panel.ordersProfile === ' '}
                          />
                          <button
                            type="button"
                            onClick={() => updatePanel(idx, { ordersProfile: '' })}
                            className="text-muted-foreground/60 hover:text-destructive shrink-0 p-0.5"
                            title="Remove profile reminder"
                            data-testid={`batch-clear-profile-${idx}`}
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    <textarea
                      value={panel.notes}
                      onFocus={() => focusModeEnabled && setFocusedPanel(idx)}
                      onChange={(e) => {
                        const val = e.target.value;
                        // Dot phrase expansion (forgiving — try exact, lowercase, with-dot, lowercase+with-dot)
                        if (val.endsWith(' ')) {
                          const words = val.trimEnd().split(/\s+/);
                          const lastWord = words[words.length - 1];
                          if (lastWord) {
                            const allPhrases = { ...DOT_PHRASES, ...customPhrases };
                            const lcMap = {};
                            Object.keys(allPhrases).forEach((k) => { lcMap[k.toLowerCase()] = allPhrases[k]; });
                            const candidates = [
                              lastWord,
                              lastWord.toLowerCase(),
                              lastWord.startsWith('.') ? lastWord : '.' + lastWord,
                              '.' + lastWord.replace(/^\./, '').toLowerCase(),
                            ];
                            let expansion = null;
                            for (const c of candidates) {
                              if (allPhrases[c]) { expansion = allPhrases[c]; break; }
                              if (lcMap[c.toLowerCase()]) { expansion = lcMap[c.toLowerCase()]; break; }
                            }
                            if (expansion) {
                              const before = val.substring(0, val.lastIndexOf(lastWord));
                              updatePanel(idx, { notes: before + expansion + ' ' });
                              toast.success(`Expanded ${lastWord}`, { duration: 1500 });
                              return;
                            }
                          }
                        }
                        updatePanel(idx, { notes: val });
                      }}
                      placeholder="Paste or type HPI, scratch notes, med list..."
                      className="flex-1 bg-input border border-border rounded-lg p-2 text-xs text-foreground placeholder:text-muted-foreground/50 resize-none outline-none focus:ring-1 focus:ring-primary/50 custom-scrollbar min-h-[80px]"
                      data-testid={`batch-notes-${idx}`}
                    />
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col p-2 gap-2 min-h-0 overflow-hidden">
                    {/* Post-generation title — same as main chart */}
                    <div className="shrink-0 px-2 py-1.5 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-between gap-2">
                      <p className="text-[10px] font-semibold text-primary truncate flex-1" data-testid={`batch-post-title-${idx}`}>
                        {panelTitles[idx]}
                      </p>
                      {/* Orders-pending alert — high-visibility reminder that
                          this note has imaging / referrals / profile the
                          provider still needs to physically place in Genesis
                          (imaging + referrals) or MEDPROS (profile). */}
                      {((panel.ordersImaging || '').trim() || (panel.ordersReferrals || '').trim() || (panel.ordersProfile || '').trim()) && (() => {
                        const oi = (panel.ordersImaging || '').trim();
                        const or = (panel.ordersReferrals || '').trim();
                        const op = (panel.ordersProfile || '').trim();
                        const lines = [];
                        if (oi) lines.push('• Imaging (Genesis): ' + oi);
                        if (or) lines.push('• Referral (Genesis): ' + or);
                        if (op) lines.push('• Profile (MEDPROS): ' + op);
                        return (
                          <span
                            className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase bg-red-500/15 text-red-400 border border-red-500/40 animate-pulse"
                            title={'Place these before signing this chart:\n' + lines.join('\n')}
                            data-testid={`batch-orders-pending-${idx}`}
                          >
                            📋 Orders pending
                          </span>
                        );
                      })()}
                      <button
                        onClick={() => {
                          safeCopyToClipboard(panelTitles[idx]);
                          toast.success('Title copied');
                        }}
                        className="shrink-0 p-0.5 rounded hover:bg-primary/20 text-primary/60 hover:text-primary transition-colors"
                        title="Copy title"
                        data-testid={`batch-copy-title-${idx}`}
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    {/* ICD-10 codes — compact: code only, hover for description, collapsed by default */}
                    {panel.icd10.length > 0 && (
                      <div className="shrink-0 bg-primary/5 border border-primary/15 rounded-lg">
                        <button 
                          onClick={() => updatePanel(idx, { icd10Collapsed: !panel.icd10Collapsed })}
                          className="w-full flex items-center justify-between px-2 py-1 hover:bg-primary/10 transition-colors rounded-lg"
                          data-testid={`batch-icd10-toggle-${idx}`}
                        >
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="text-[9px] font-bold text-primary">ICD-10</span>
                            {panel.icd10.slice(0, 4).map((code, ci) => (
                              <span key={ci} className="text-[9px] font-mono font-semibold text-primary/80 bg-primary/10 px-1 rounded" title={`${code.code}: ${code.description}`}>{code.code}</span>
                            ))}
                            {panel.icd10.length > 4 && <span className="text-[9px] text-primary/60">+{panel.icd10.length - 4}</span>}
                          </div>
                          <ChevronDown className={`w-3 h-3 text-primary shrink-0 transition-transform ${panel.icd10Collapsed ? '' : 'rotate-180'}`} />
                        </button>
                        {!panel.icd10Collapsed && (
                          <div className="px-2 pb-1.5 flex flex-wrap gap-1">
                          {panel.icd10.map((code, ci) => (
                            <div key={ci} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold bg-primary/15 text-primary border border-primary/20" title={`${code.code}: ${code.description}`}>
                              <div className="flex flex-col -my-0.5 mr-0.5">
                                <button
                                  onClick={() => {
                                    if (ci === 0) return;
                                    const updated = [...panel.icd10];
                                    [updated[ci - 1], updated[ci]] = [updated[ci], updated[ci - 1]];
                                    updatePanel(idx, { icd10: updated });
                                  }}
                                  disabled={ci === 0}
                                  className="p-0 w-2.5 h-2.5 inline-flex items-center justify-center text-primary/60 hover:text-primary disabled:opacity-20"
                                  data-testid={`batch-icd10-up-${idx}-${ci}`}
                                >
                                  <ChevronUp className="w-2 h-2" />
                                </button>
                                <button
                                  onClick={() => {
                                    if (ci === panel.icd10.length - 1) return;
                                    const updated = [...panel.icd10];
                                    [updated[ci], updated[ci + 1]] = [updated[ci + 1], updated[ci]];
                                    updatePanel(idx, { icd10: updated });
                                  }}
                                  disabled={ci === panel.icd10.length - 1}
                                  className="p-0 w-2.5 h-2.5 inline-flex items-center justify-center text-primary/60 hover:text-primary disabled:opacity-20"
                                  data-testid={`batch-icd10-down-${idx}-${ci}`}
                                >
                                  <ChevronDown className="w-2 h-2" />
                                </button>
                              </div>
                              {code.code}
                              <button
                                onClick={() => {
                                  const updated = [...panel.icd10];
                                  updated.splice(ci, 1);
                                  updatePanel(idx, { icd10: updated });
                                }}
                                className="ml-0.5 p-0 w-2.5 h-2.5 rounded-full bg-destructive/20 hover:bg-destructive/40 text-destructive inline-flex items-center justify-center opacity-60 hover:opacity-100"
                                data-testid={`batch-icd10-delete-${idx}-${ci}`}
                              >
                                <X className="w-2 h-2" />
                              </button>
                            </div>
                          ))}
                          </div>
                        )}
                      </div>
                    )}
                    {/* LLM-only suggested codes — ghost row. Feb 16, 2026:
                        added to stop the LLM from auto-merging codes into
                        the provider's locked list. One tap promotes a
                        suggestion to a real chip; "Dismiss" clears them all. */}
                    {panel.llmSuggestions && panel.llmSuggestions.length > 0 && (
                      <div className="shrink-0 bg-amber-500/5 border border-amber-500/20 rounded-lg px-2 py-1.5"
                           data-testid={`batch-llm-suggestions-${idx}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[9px] font-bold text-amber-400">
                            LLM also flagged ({panel.llmSuggestions.length}) — tap to add
                          </span>
                          <button
                            type="button"
                            onClick={() => updatePanel(idx, { llmSuggestions: [] })}
                            className="text-[9px] text-amber-400/70 hover:text-amber-300 underline-offset-2 hover:underline"
                            data-testid={`batch-llm-suggestions-dismiss-${idx}`}
                          >
                            Dismiss all
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {panel.llmSuggestions.map((sug, si) => (
                            <button
                              key={`${sug.code}-${si}`}
                              type="button"
                              onClick={() => {
                                // Promote to locked list, remove from suggestions
                                const updatedCodes = [...(panel.icd10 || []), sug];
                                const updatedSugs = (panel.llmSuggestions || []).filter((_, i) => i !== si);
                                updatePanel(idx, { icd10: updatedCodes, llmSuggestions: updatedSugs });
                              }}
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono font-semibold bg-amber-500/10 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 transition-colors"
                              title={`Tap to add ${sug.code} — ${sug.description}`}
                              data-testid={`batch-llm-suggestion-add-${idx}-${si}`}
                            >
                              <span className="text-amber-400">+</span>
                              {sug.code}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* "Did you mean?" hint pills. Feb 16, 2026 — symptom→code
                        mismatch heuristic. Catches LLM picking E28.2 (PCOS)
                        when notes say "dysmenorrhea" (should be N94.6). */}
                    {(() => {
                      const allCodes = [...(panel.icd10 || []), ...(panel.llmSuggestions || [])];
                      const hints = detectIcd10Hints(panel.notes || '', allCodes);
                      if (hints.length === 0) return null;
                      return (
                        <div className="shrink-0 bg-blue-500/5 border border-blue-500/30 rounded-lg px-2 py-1.5 space-y-1"
                             data-testid={`batch-icd10-hints-${idx}`}>
                          <div className="text-[9px] font-bold text-blue-400 flex items-center gap-1">
                            <AlertTriangle className="w-2.5 h-2.5" />
                            Did you mean? ({hints.length})
                          </div>
                          <div className="space-y-1">
                            {hints.map((h, hi) => (
                              <div key={`${h.suggestedCode}-${hi}`} className="flex items-center gap-1 text-[10px]">
                                {h.type === 'wrong_code' ? (
                                  <>
                                    <span className="text-muted-foreground line-through font-mono text-[9px]">{h.currentCode}</span>
                                    <span className="text-blue-400/60">→</span>
                                    <span className="text-blue-300 font-mono font-semibold">{h.suggestedCode}</span>
                                    <span className="text-blue-300/80 truncate flex-1">({h.suggestedDesc})</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Swap wrong code for preferred in panel.icd10
                                        const swapped = (panel.icd10 || []).map((c) =>
                                          (c.code || '').toUpperCase() === h.currentCode.toUpperCase()
                                            ? { code: h.suggestedCode, description: h.suggestedDesc, relevance: 'user_corrected' }
                                            : c
                                        );
                                        // If the wrong code was in llmSuggestions instead, remove it from there and add corrected to icd10
                                        const fromSugs = (panel.llmSuggestions || []).filter(
                                          (c) => (c.code || '').toUpperCase() !== h.currentCode.toUpperCase()
                                        );
                                        const alreadyInLocked = swapped.some(c => (c.code || '').toUpperCase() === h.suggestedCode.toUpperCase());
                                        const finalLocked = alreadyInLocked ? swapped : [...swapped, { code: h.suggestedCode, description: h.suggestedDesc, relevance: 'user_corrected' }];
                                        updatePanel(idx, { icd10: finalLocked, llmSuggestions: fromSugs });
                                        toast.success(`Swapped ${h.currentCode} → ${h.suggestedCode}`);
                                      }}
                                      className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-500/15 hover:bg-blue-500/30 text-blue-300 border border-blue-500/40"
                                      data-testid={`batch-hint-swap-${idx}-${hi}`}
                                    >
                                      Swap
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <span className="text-muted-foreground italic text-[9px]">missing:</span>
                                    <span className="text-blue-300 font-mono font-semibold">{h.suggestedCode}</span>
                                    <span className="text-blue-300/80 truncate flex-1">({h.suggestedDesc})</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newCode = { code: h.suggestedCode, description: h.suggestedDesc, relevance: 'user_added' };
                                        updatePanel(idx, { icd10: [...(panel.icd10 || []), newCode] });
                                        toast.success(`Added ${h.suggestedCode}`);
                                      }}
                                      className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-500/15 hover:bg-blue-500/30 text-blue-300 border border-blue-500/40"
                                      data-testid={`batch-hint-add-${idx}-${hi}`}
                                    >
                                      + Add
                                    </button>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    {/* Clinical note (live-updates during streaming) */}
                    <textarea
                      value={panel.output}
                      onChange={(e) => updatePanel(idx, { output: e.target.value })}
                      readOnly={panel.isStreaming}
                      className={`flex-1 bg-input rounded-lg p-2 text-xs text-foreground resize-none outline-none custom-scrollbar min-h-[80px] transition-colors ${
                        panel.isStreaming
                          ? 'border border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/60'
                          : 'border border-border focus:ring-1 focus:ring-primary/50'
                      }`}
                      data-testid={`batch-output-${idx}`}
                    />
                    {panel.isStreaming && (
                      <span
                        className="self-start inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 text-[9px] font-bold tracking-wider uppercase"
                        data-testid={`batch-streaming-pill-${idx}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                        STREAMING
                        {panel.ttfMs > 0 && (
                          <span className="opacity-80 normal-case font-mono ml-0.5">
                            ttft {(panel.ttfMs / 1000).toFixed(2)}s
                          </span>
                        )}
                      </span>
                    )}
                    {/* Feb 27, 2026 (v6.8.17) — Post-output action bar:
                        ↻ Regenerate (preserves manual edits) · 👁 Peek notes ·
                        EDITED badge · ↺ Reset to LLM original. Visible only
                        when output exists. */}
                    {(() => {
                      const isEdited = !!(panel.outputOriginal && panel.output && panel.output !== panel.outputOriginal);
                      return (
                        <div className="shrink-0 flex items-center gap-1.5 flex-wrap" data-testid={`batch-post-actions-${idx}`}>
                          <button
                            type="button"
                            onClick={() => {
                              if (panel.isStreaming && streamAbortersRef.current[idx]) {
                                // Stop the in-flight stream — keeps whatever
                                // tokens already painted into the textarea.
                                streamAbortersRef.current[idx].abort();
                                return;
                              }
                              generateNote(idx);
                            }}
                            disabled={panel.isGenerating && !panel.isStreaming}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold border transition-all ${
                              panel.isStreaming
                                ? 'border-red-400/70 bg-red-500/20 text-red-200 hover:bg-red-500/30 animate-pulse'
                                : panel.isGenerating
                                  ? 'opacity-50 cursor-not-allowed border-border/40 text-muted-foreground'
                                  : isEdited
                                    ? 'border-amber-400/60 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25'
                                    : 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/20'
                            }`}
                            data-testid={`batch-regenerate-${idx}`}
                            title={panel.isStreaming
                              ? 'Stop streaming — keep what is rendered'
                              : isEdited
                                ? `Regenerate — your ${(panel.output || '').length - (panel.outputOriginal || '').length > 0 ? 'additions' : 'edits'} will be preserved (sent as prior_output_edits)`
                                : 'Regenerate this note'}
                          >
                            {panel.isStreaming ? (
                              <>
                                <span className="w-2 h-2 bg-red-200 rounded-sm" />
                                <span>Stop ({panel.genTime}s)</span>
                              </>
                            ) : panel.isGenerating ? (
                              <>
                                <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                <span>{panel.genTime}s…</span>
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-2.5 h-2.5" />
                                <span>Regenerate</span>
                                {isEdited && <span className="ml-0.5 text-[8px] font-mono bg-amber-500/30 px-1 rounded">+edits</span>}
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => updatePanel(idx, { notesPeek: !panel.notesPeek })}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold border transition-all ${
                              panel.notesPeek
                                ? 'border-blue-400/60 bg-blue-500/15 text-blue-200'
                                : 'border-border/60 text-muted-foreground hover:text-foreground hover:border-border'
                            }`}
                            data-testid={`batch-notes-peek-${idx}`}
                            title="Toggle a peek view of the original dictation (does not affect output)"
                          >
                            <Eye className="w-2.5 h-2.5" />
                            <span>{panel.notesPeek ? 'Hide notes' : 'Peek notes'}</span>
                          </button>
                          {isEdited && (
                            <>
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/20 text-amber-200 border border-amber-400/40"
                                data-testid={`batch-edited-badge-${idx}`}
                                title="You've edited the LLM's output. Regenerate will preserve your additions."
                              >
                                EDITED
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm('Reset to LLM original? Your edits will be discarded.')) {
                                    updatePanel(idx, { output: panel.outputOriginal });
                                  }
                                }}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold text-muted-foreground hover:text-destructive hover:bg-destructive/10 border border-border/60 transition-all"
                                data-testid={`batch-reset-llm-${idx}`}
                                title="Discard edits, restore the LLM's original output"
                              >
                                <RotateCcw className="w-2.5 h-2.5" />
                                Reset
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })()}
                    {/* Original-dictation peek panel — read-only, collapsible. */}
                    {panel.notesPeek && (
                      <div
                        className="shrink-0 bg-blue-500/5 border border-blue-500/30 rounded-lg p-2 max-h-[100px] overflow-y-auto custom-scrollbar"
                        data-testid={`batch-notes-peek-panel-${idx}`}
                      >
                        <div className="text-[9px] font-bold text-blue-400/80 uppercase tracking-wider mb-1">
                          Original dictation (read-only)
                        </div>
                        <pre className="text-[10px] text-foreground/85 whitespace-pre-wrap font-sans">
                          {panel.notes || '(empty)'}
                        </pre>
                      </div>
                    )}
                    {/* Signature block — speed version: modality picker + note completed + DOS */}
                    <div className="shrink-0 bg-secondary/30 border border-border/50 rounded-lg px-2 py-1.5 space-y-1" data-testid={`batch-sig-block-${idx}`}>
                      {/* Row 1: Modality toggle + CPT code + Combo picker */}
                      <div className="flex items-center justify-between gap-1.5">
                        <div className="flex items-center gap-1">
                          <div className="flex items-center gap-0.5 bg-secondary/60 rounded p-0.5 border border-border/40">
                            {SPEED_CPT_OPTIONS.map(opt => (
                              <button
                                key={opt.key}
                                onClick={() => updatePanel(idx, {
                                  modality: opt.key,
                                  // Bottom toggle is the source of truth for
                                  // modality. Keep visitType in sync so the
                                  // backend gets the right include_pe flag.
                                  visitType: opt.key === 'in_person' ? 'in_person_exam' : 'virtual',
                                  modalityManuallySet: true,
                                })}
                                className={`px-1.5 py-0.5 rounded text-[9px] font-bold transition-all ${
                                  panel.modality === opt.key
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                                }`}
                                data-testid={`batch-modality-${opt.key}-${idx}`}
                              >
                                {opt.shortLabel}
                              </button>
                            ))}
                          </div>
                          {cptCombos.length > 0 && (
                            <div className="relative">
                              <button
                                onClick={() => setShowCptCombos(showCptCombos === idx ? null : idx)}
                                className="px-1.5 py-0.5 rounded text-[8px] font-semibold bg-amber-500/10 border border-amber-500/25 text-amber-300 hover:bg-amber-500/20 transition-all"
                                data-testid={`batch-combo-toggle-${idx}`}
                                title="Apply CPT Combo"
                              >
                                <Bookmark className="w-2.5 h-2.5 inline mr-0.5" />Combo
                              </button>
                              {showCptCombos === idx && (
                                <div className="absolute left-0 top-full mt-1 z-50 bg-card border border-amber-500/30 rounded-lg shadow-xl py-1 min-w-[160px]" data-testid={`batch-combo-dropdown-${idx}`}>
                                  {cptCombos.map(combo => (
                                    <button
                                      key={combo.id}
                                      onClick={() => {
                                        const modalityKey = combo.modality || 'in_person';
                                        updatePanel(idx, {
                                          modality: modalityKey,
                                          visitType: modalityKey === 'in_person' ? 'in_person_exam' : 'virtual',
                                          modalityManuallySet: true,
                                        });
                                        setShowCptCombos(null);
                                        toast.success(`Applied "${combo.name}"`, { duration: 1200 });
                                        axios.post(`${API}/api/cpt-combos/${combo.id}/use`, {}, { withCredentials: true }).catch(() => {});
                                      }}
                                      className="w-full text-left px-3 py-1.5 text-[10px] hover:bg-amber-500/10 transition-colors flex items-center gap-2"
                                      data-testid={`batch-combo-apply-${combo.id}`}
                                    >
                                      <span className="font-semibold text-amber-300">{combo.name}</span>
                                      <span className="text-[8px] text-muted-foreground">{combo.codes.map(c => c.code).join('+')}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <span className="text-[9px] font-mono font-bold text-foreground/70" data-testid={`batch-cpt-code-${idx}`}>
                          CPT {SPEED_CPT_OPTIONS.find(o => o.key === panel.modality)?.code || '99214'}
                        </span>
                      </div>
                      {/* Row 2: Note completed + DOS */}
                      <div className="flex items-center justify-between text-[9px] text-foreground/60 font-mono">
                        <div className="flex items-center gap-1">
                          <FileSignature className="w-2.5 h-2.5 text-primary/50" />
                          <span>Note completed</span>
                        </div>
                        <span>DOS: {panel.apptDate || new Date().toLocaleDateString()} {panel.apptTime || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={() => {
                          safeCopyToClipboard(buildPanelText(panel, idx));
                          updatePanel(idx, { copied: true });
                          bumpCounter();
                          setTimeout(() => updatePanel(idx, { copied: false }), 2000);
                        }}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5 border ${
                          panel.copied
                            ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-400'
                            : 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/20'
                        }`}
                        data-testid={`batch-copy-${idx}`}
                      >
                        {panel.copied ? <><CheckCircle2 className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                      </button>
                      <button
                        onClick={() => saveAsTemplate(idx)}
                        className="px-2 py-1.5 rounded-lg text-xs font-medium border border-amber-500/30 text-amber-400 hover:bg-amber-500/15 transition-colors"
                        title="Save as template"
                        data-testid={`batch-save-template-${idx}`}
                      >
                        <BookmarkPlus className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => { updatePanel(idx, { output: null }); }}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium border border-border text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                        data-testid={`batch-edit-${idx}`}
                      >
                        <RefreshCw className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );})}
        </div>

        {/* Session History Bar */}
        <div className="shrink-0 border-t border-border bg-card" data-testid="session-history-bar">
          {/* Toggle header */}
          <button
            onClick={() => setShowSessionHistory(prev => !prev)}
            className="w-full flex items-center justify-between px-4 py-2 hover:bg-secondary/30 transition-colors"
            data-testid="session-history-toggle"
          >
            <div className="flex items-center gap-2">
              <Archive className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-bold text-foreground">Session Notes</span>
              <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-amber-500/40 text-amber-400">
                {sessionHistory.length}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              {sessionHistory.length > 0 && !showSessionHistory && (
                <span className="text-[10px] text-muted-foreground">
                  {sessionHistory.map(n => n.label || 'Pt').join(' · ')}
                </span>
              )}
              <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showSessionHistory ? 'rotate-90' : ''}`} />
            </div>
          </button>

          {/* Expanded session history */}
          {showSessionHistory && (
            <div className="border-t border-border/50 max-h-[280px] overflow-y-auto custom-scrollbar">
              {/* Actions bar */}
              {sessionHistory.length > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-secondary/20 border-b border-border/50 flex-wrap">
                  <button
                    onClick={selectAllSessionNotes}
                    className="px-2 py-1 rounded text-[10px] font-semibold border border-border hover:bg-secondary/50 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="session-select-all"
                  >
                    {selectedSessionNotes.size === sessionHistory.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <button
                    onClick={copyAllSessionNotes}
                    className="px-2 py-1 rounded text-[10px] font-semibold border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors flex items-center gap-1"
                    data-testid="session-copy-all"
                  >
                    <Copy className="w-3 h-3" />
                    Copy All ({sessionHistory.length})
                  </button>
                  {selectedSessionNotes.size > 0 && (
                    <button
                      onClick={copySelectedSessionNotes}
                      className="px-2 py-1 rounded text-[10px] font-semibold border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 transition-colors flex items-center gap-1"
                      data-testid="session-copy-selected"
                    >
                      <Copy className="w-3 h-3" />
                      Copy Selected ({selectedSessionNotes.size})
                    </button>
                  )}
                  <button
                    onClick={() => downloadSessionNotes('docx')}
                    className="px-2 py-1 rounded text-[10px] font-semibold border border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors flex items-center gap-1"
                    data-testid="session-download-docx"
                  >
                    <Download className="w-3 h-3" />
                    DOCX {selectedSessionNotes.size > 0 ? `(${selectedSessionNotes.size})` : `(${sessionHistory.length})`}
                  </button>
                  <button
                    onClick={() => downloadSessionNotes('pdf')}
                    className="px-2 py-1 rounded text-[10px] font-semibold border border-purple-500/30 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 transition-colors flex items-center gap-1"
                    data-testid="session-download-pdf"
                  >
                    <Download className="w-3 h-3" />
                    PDF {selectedSessionNotes.size > 0 ? `(${selectedSessionNotes.size})` : `(${sessionHistory.length})`}
                  </button>
                  <div className="flex-1" />
                  <button
                    onClick={clearSessionHistory}
                    className="px-2 py-1 rounded text-[10px] font-semibold border border-destructive/30 text-destructive/70 hover:bg-destructive/10 hover:text-destructive transition-colors"
                    data-testid="session-clear"
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Notes list */}
              <div className="p-2 space-y-1">
                {sessionHistory.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    Notes will appear here as you complete batches. Hit "Next {gridSize}" to save current batch.
                  </p>
                ) : (
                  sessionHistory.map((note, ni) => (
                    <div
                      key={note.id}
                      className={`flex items-start gap-2 p-2 rounded-lg transition-all cursor-pointer ${
                        selectedSessionNotes.has(note.id)
                          ? 'bg-primary/10 border border-primary/30'
                          : 'bg-secondary/20 border border-transparent hover:bg-secondary/40'
                      }`}
                      onClick={() => toggleSessionNoteSelection(note.id)}
                      data-testid={`session-note-${ni}`}
                    >
                      <div className={`shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 transition-colors ${
                        selectedSessionNotes.has(note.id)
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background'
                      }`}>
                        {selectedSessionNotes.has(note.id) && <Check className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-semibold text-foreground truncate">{note.title || note.label || `Note ${ni + 1}`}</p>
                          {note.icd10?.length > 0 && (
                            <Badge variant="outline" className="text-[8px] h-4 px-1 border-primary/30 text-primary shrink-0">
                              {note.icd10.length} ICD-10
                            </Badge>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                          {note.output?.substring(0, 100)}...
                        </p>
                        <p className="text-[9px] text-muted-foreground/60 mt-0.5">
                          {note.savedAt ? new Date(note.savedAt).toLocaleTimeString() : ''}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          safeCopyToClipboard(buildSessionNoteText(note));
                          toast.success('Note copied');
                        }}
                        className="shrink-0 p-1 rounded hover:bg-primary/20 text-muted-foreground hover:text-primary transition-colors"
                        title="Copy this note"
                        data-testid={`session-note-copy-${ni}`}
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSessionNote(note.id);
                        }}
                        className="shrink-0 p-1 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors"
                        title="Remove from session"
                        data-testid={`session-note-remove-${ni}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
  );
};

export default BatchChartingGrid;
